/*
 * business-card/index.js — the constructor, wired up.
 *
 * TWO UX DECISIONS EVERYTHING ELSE FOLLOWS FROM
 * ─────────────────────────────────────────────
 *
 * 1. THE PAGE OPENS WITH A FINISHED CARD, not an empty canvas. The nearest
 *    sibling measured it: /ticket-generator over 30 days took 1022 sessions to
 *    the page and 254 to a first generate — three quarters left without
 *    producing anything, and the largest abandon bucket was `idle`. A blank
 *    canvas asks the visitor to be a designer before it gives them anything; a
 *    finished card asks them to replace a name.
 *
 * 2. THE CARD IS THE INTERFACE. The first
 *    build was a form that produced a card — tabs on top, fields below. On the
 *    shipped page that measured: five of seven panels hidden behind tabs at any
 *    moment, the export button 3.4 screens down on a laptop and 7.0 on a phone,
 *    and a click on the card doing nothing. So: the card column
 *    carries its own actions; the controls are <details> sections with live
 *    summaries rather than tabs, so nothing is hidden; and the card itself is
 *    clickable, draggable and directly editable (see interact.js).
 *
 * WHAT THE STATE IS
 * ─────────────────
 * One `doc` object, serialisable, which is also the save file (.tmbcard). Every
 * render is a pure function of it, and every mutation goes through `commit()`
 * so undo is free rather than bolted on.
 */
(function () {
    'use strict';

    var SLUG = 'business-card-maker';
    var $ = function (sel, r) { return (r || document).querySelector(sel); };
    var $$ = function (sel, r) { return Array.prototype.slice.call((r || document).querySelectorAll(sel)); };
    var T = (window.__BUSINESS_CARD__ && window.__BUSINESS_CARD__.i18n) || {};
    function t(k, sub) {
        var s = T[k] != null ? T[k] : k;
        if (sub) for (var p in sub) s = s.replace(new RegExp(':' + p, 'g'), sub[p]);
        return s;
    }

    var Spec = window.CardSpec, Ink = window.CardInk, Presets = window.CardPresets,
        Layout = window.CardLayout, Vcard = window.CardVcard, Pre = window.CardPreflight,
        Render = window.CardRender, Pdf = window.CardPdf, LL = window.LabelLayout,
        Interact = window.CardInteract;

    var FACES = [
        { id: 'montserrat', css: 'BC Montserrat' }, { id: 'noto', css: 'BC Noto Sans' },
        { id: 'oswald', css: 'BC Oswald' }, { id: 'playfair', css: 'BC Playfair' },
        { id: 'comfortaa', css: 'BC Comfortaa' }, { id: 'russoone', css: 'BC Russo One' },
        { id: 'yeseva', css: 'BC Yeseva One' }, { id: 'caveat', css: 'BC Caveat' },
    ];
    function faceCss(id) {
        for (var i = 0; i < FACES.length; i++) if (FACES[i].id === id) return FACES[i].css;
        return FACES[0].css;
    }

    /* ── the document ───────────────────────────────────────────────────────*/
    function defaultDoc() {
        return {
            v: 2,
            format: (window.__BUSINESS_CARD__ && window.__BUSINESS_CARD__.defaultFormat) || 'ru-90x50',
            portrait: false, radius: 0, bleed: null, safe: null,
            palette: 'navy', customHex: '#1B3A5C', preset: 'classic', back: 'logo',
            fonts: { heading: 'montserrat', body: 'montserrat' },
            scale: 1,
            data: sampleData(),
            logo: null,
            /* Per SIDE, because the front and the back are two pictures: the
             * commonest photo card in the world is a photograph on the back and
             * clean paper on the front. One shared background would make that
             * layout impossible to express. */
            bg: { front: null, back: null },
            qr: { enabled: false, mode: 'vcard', url: '', text: '', size: 16,
                  ecc: 'M', side: 'front', x: null, y: null },
            /* Per-element adjustments, keyed by the stable element id from
             * layout.js. Separated by side because the same id exists on both. */
            overrides: { front: {}, back: {} },
        };
    }
    function sampleData() {
        var s = (window.__BUSINESS_CARD__ && window.__BUSINESS_CARD__.sample) || {};
        return {
            name: s.name || '', role: s.role || '', company: s.company || '',
            tagline: s.tagline || '', phone: s.phone || '', phone2: '',
            email: s.email || '', site: s.site || '', address: s.address || '',
            telegram: '', whatsapp: '',
        };
    }

    var doc = defaultDoc();
    var pristine = true;

    /* ── the sample, tracked instead of destroyed ────────────────────────────
     * The page opens on a finished card so the first screen looks like a
     * business card rather than an empty form. The risk that creates is real —
     * somebody replaces the name and prints a thousand cards carrying OUR
     * phone number — but the first answer to it was to WIPE every other field
     * on the first keystroke, and that answer was reported as a bug the day
     * after it shipped: «я пытался отредактировать выбранный текст, а у меня
     * стерлись вообще все поля». Silent, total, and with no visible way back.
     *
     * Now the sample is remembered and reported by preflight, which is the
     * mechanism this tool already uses for every other way a card can be wrong.
     * `SAMPLE` is the immutable original; `sampleLeft()` derives the answer
     * from the CURRENT data every time rather than keeping a second flag that
     * can fall out of step with it (undo, project load and draft restore all
     * replace `doc.data` wholesale, and each of them would have to remember to
     * update such a flag). */
    var SAMPLE = sampleData();
    var SAMPLE_KEYS = Object.keys(SAMPLE).filter(function (k) { return !!SAMPLE[k]; });

    function sampleLeft() {
        var d = doc.data || {};
        return SAMPLE_KEYS.filter(function (k) { return d[k] && d[k] === SAMPLE[k]; });
    }
    /* "The user has begun making this card their own." Anything else is the
     * opening screen, where saying "these fields are ours" is noise. */
    function sampleTouched() { return sampleLeft().length < SAMPLE_KEYS.length; }
    function sampleState() { return { fields: sampleLeft(), touched: sampleTouched() }; }

    /* One click for what the wipe used to do without asking. */
    function clearSample() {
        var left = sampleLeft();
        if (!left.length) return;
        pushUndo();
        left.forEach(function (f) {
            doc.data[f] = '';
            var e = $('#bc-f-' + f); if (e) e.value = '';
        });
        pristine = false;
        qrCache = {};
        feature('sample', 'cleared', { n: left.length });
        draw();
    }

    function syncSampleUi() {
        var row = $('#bc-sample-row');
        if (!row) return;
        var st = sampleState();
        row.hidden = !(st.touched && st.fields.length);
        var n = $('#bc-sample-note');
        if (n && !row.hidden) n.textContent = t('sample_left_hint', { n: st.fields.length });
    }

    var side = 'front';
    var images = {};
    var qrCache = {};
    var qrLib = null;
    var lastBuild = null;
    var lastIssues = [];
    var lastPre = null;
    var interact = {};                   // side → controller
    var measure = null;

    /* ── undo ────────────────────────────────────────────────────────────────
     * Snapshots of the whole document. A card is a few kilobytes of JSON minus
     * the logo, so diffing would be more code and more bugs for no benefit —
     * and undo that is occasionally wrong is worse than none. The logo data URL
     * is shared by reference rather than copied, which is what keeps a snapshot
     * cheap when someone has uploaded a 2 MB PNG. */
    var undoStack = [], redoStack = [], MAX_UNDO = 60;
    function snapshot() {
        /* ⚠️ THE PICTURES ARE LIFTED OUT BEFORE THE CLONE, not after. A logo
         * and a background are data URLs of megabytes, an undo stack is sixty
         * deep, and `JSON.parse(JSON.stringify(doc))` would make a private copy
         * of every one of them — a hundred megabytes of identical base64 for a
         * card somebody nudged sixty times. They are immutable once chosen, so
         * sharing the string is safe and copying it is pure waste. */
        var logo = doc.logo;
        var bg = doc.bg || { front: null, back: null };
        var srcs = { front: bg.front && bg.front.src, back: bg.back && bg.back.src };
        doc.logo = null;
        if (bg.front) bg.front.src = '';
        if (bg.back) bg.back.src = '';
        var s = JSON.parse(JSON.stringify(doc));
        doc.logo = logo;
        if (bg.front) bg.front.src = srcs.front;
        if (bg.back) bg.back.src = srcs.back;
        s.logo = logo ? { src: logo.src, mono: !!logo.mono } : null;
        if (s.bg) {
            if (s.bg.front) s.bg.front.src = srcs.front;
            if (s.bg.back) s.bg.back.src = srcs.back;
        }
        return s;
    }
    function pushUndo() {
        undoStack.push(snapshot());
        if (undoStack.length > MAX_UNDO) undoStack.shift();
        redoStack.length = 0;
        syncUndoButtons();
    }
    function applySnapshot(s) {
        doc = s;
        if (doc.logo && doc.logo.src) preloadImage(doc.logo.src, draw);
        ['front', 'back'].forEach(function (w) {
            var b = doc.bg && doc.bg[w];
            if (b && b.src) preloadImage(b.src, draw);
        });
        qrCache = {};
        syncFormFromDoc();
        draw();
    }
    function undo() {
        if (!undoStack.length) return;
        redoStack.push(snapshot());
        applySnapshot(undoStack.pop());
        syncUndoButtons();
    }
    function redo() {
        if (!redoStack.length) return;
        undoStack.push(snapshot());
        applySnapshot(redoStack.pop());
        syncUndoButtons();
    }
    function syncUndoButtons() {
        var u = $('#bc-undo'), r = $('#bc-redo');
        if (u) u.disabled = !undoStack.length;
        if (r) r.disabled = !redoStack.length;
    }

    /* ── geometry + build ───────────────────────────────────────────────────*/
    function fmt() { return Spec.byId(doc.format) || Spec.all()[0]; }
    function boxes() {
        return Spec.boxes(fmt(), { bleed: doc.bleed, safe: doc.safe, portrait: doc.portrait });
    }
    /* A brand colour is a palette of one: the other three roles are derived
     * from it in CMYK (see ink.js), because four independently chosen colours
     * fight and a company that owns a blue does not own the greys around it. */
    function palette() {
        return doc.palette === 'custom' ? Ink.customPalette(doc.customHex) : Ink.paletteById(doc.palette);
    }
    /* ⚠️ The chosen typeface is a LATIN design. It is first in the stack because
     * that is what the user picked, but the moment a character is Arabic or
     * Korean the browser has to fall back — and it must fall back to the SAME
     * ladder the exporter walks, or the screen and the paper stop being the
     * same card (measured 2026-08-24: 14% apart on an Arabic line). */
    function familyFor(style) {
        var bold = !!(style && style.weight >= 600);
        var mine = '"' + faceCss(bold ? doc.fonts.heading : doc.fonts.body) + '"';
        var fallback = (window.PdfTextFont && window.PdfTextFont.cssStack)
            ? window.PdfTextFont.cssStack(bold) : '';
        return mine + (fallback ? ', ' + fallback : '') + ', sans-serif';
    }

    function qrInfo() {
        if (!doc.qr.enabled) return null;
        var count = qrLib ? function (text, ecc) { return Pdf.qrMatrix(qrLib, text, ecc).n; } : null;
        return Vcard.build(doc.data, doc.qr.size, {
            ecc: doc.qr.ecc, mode: doc.qr.mode, url: doc.qr.url, text: doc.qr.text,
            countModules: count,
        });
    }
    /* What the logo will look like once it is ink.
     *
     * Built ONCE per file rather than per redraw: the conversion is a search
     * per distinct colour (17 ms for a flat mark, 39 ms for a photograph after
     * bucketing), which is nothing once and unacceptable on every keystroke.
     * Cached by source, so switching sides or retyping a phone number costs
     * nothing. */
    var printSim = {};
    var printShift = {};

    function buildPrintSim(src) {
        var img = images[src];
        if (!img || !img.naturalWidth || !window.CardInk || !CardInk.simulateInPlace) return;
        var W = Math.min(1200, img.naturalWidth);
        var H = Math.max(1, Math.round(W * img.naturalHeight / img.naturalWidth));
        var cv = document.createElement('canvas');
        cv.width = W; cv.height = H;
        var g = cv.getContext('2d', { willReadFrequently: true });
        g.drawImage(img, 0, 0, W, H);
        var d;
        try { d = g.getImageData(0, 0, W, H); } catch (e) { return; }   // tainted canvas
        var r = CardInk.simulateInPlace(d.data, W, H);
        g.putImageData(d, 0, 0);
        printSim[src] = cv;
        printShift[src] = r.shift;
    }

    /* ── the background photograph ───────────────────────────────────────────
     * Three jobs, all of them caching, because the expensive part is not the
     * blur — it is the trip through the ink model that makes the preview show
     * the PRINTED colour. Measured on the logo path: a photograph is a search
     * per distinct colour, 39 ms bucketed. Nothing that costs 39 ms may run on
     * a keystroke, and a slider is nothing but keystrokes.
     *
     * So a bake is keyed by everything that changes a pixel (see
     * `CardPhoto.key`) plus the output size, and a redraw that changes neither
     * — typing a phone number, switching sides — reuses the canvas.
     */
    var photoCache = {};
    var photoOrder = [];
    /* 16, not 8: the veil search bakes the card eight times in a row at 240 px,
     * and at eight slots those eight evicted every preview canvas — so the
     * redraw right after an upload re-baked and re-simulated the full-size
     * photograph for nothing. Sixteen small canvases cost a few megabytes. */
    var PHOTO_CACHE_MAX = 16;
    /* A 600 dpi PNG bake is ~2000x1200 and is wanted exactly once. Letting it
     * into the cache would evict the preview's own canvases and make the next
     * keystroke re-bake and re-simulate the whole photograph. */
    var PHOTO_CACHE_MAX_PX = 900 * 900;

    function photoHost() {
        return {
            ready: function (src) {
                var i = images[src];
                return !!(i && i.complete && i.naturalWidth);
            },
            nat: function (src) {
                var i = images[src];
                return i ? { w: i.naturalWidth, h: i.naturalHeight } : null;
            },
            rgb: function (c) {
                if (!c || !window.CardInk) return [255, 255, 255];
                return CardInk.cmykToRgb(c.c, c.m, c.y, c.k);
            },
            meta: function (spec, place, colours) {
                var CP = window.CardPhoto;
                if (!CP) return { dpi: 0, pkey: '' };
                var i = images[spec.src];
                var nat = i ? { naturalWidth: i.naturalWidth, naturalHeight: i.naturalHeight }
                            : { naturalWidth: 1, naturalHeight: 1 };
                return {
                    dpi: CP.sourceRect(nat, place, spec).dpi,
                    pkey: CP.key(spec, place, colours),
                };
            },
        };
    }

    /* Bake every background this draw needs, at the size it will be drawn, and
     * hand the canvases to the renderer. The print simulation runs on the BAKED
     * pixels, never on the file: veiling a photograph 40 % toward paper changes
     * every colour in it, and simulating the original would preview a picture
     * that is not the one being printed. */
    function bakePhotos(built, pxPerMm) {
        var CP = window.CardPhoto;
        var out = {};
        if (!CP) return out;
        ['front', 'back'].forEach(function (which) {
            (built[which].items || []).forEach(function (it) {
                if (it.type !== 'photo' || !it.pkey) return;
                var W = Math.max(1, Math.round(it.w * pxPerMm));
                var H = Math.max(1, Math.round(it.h * pxPerMm));
                var ck = it.pkey + '@' + W + 'x' + H;
                if (!photoCache[ck]) {
                    var img = images[it.src];
                    if (!img || !img.complete || !img.naturalWidth) return;
                    var cv = CP.bake(img, it.spec, it.place, it.colours, { w: W, h: H });
                    simulateCanvas(cv);
                    if (W * H > PHOTO_CACHE_MAX_PX) { out[it.pkey] = cv; return; }
                    photoCache[ck] = cv;
                    photoOrder.push(ck);
                    while (photoOrder.length > PHOTO_CACHE_MAX) delete photoCache[photoOrder.shift()];
                }
                out[it.pkey] = photoCache[ck];
            });
        });
        return out;
    }

    /* The same press model the palette and the logo already go through. Failing
     * quietly is deliberate: a tainted canvas means an image the browser will
     * not let us read, and an unsimulated photograph is a small lie where no
     * photograph at all is a broken card. */
    function simulateCanvas(cv) {
        if (!window.CardInk || !CardInk.simulateInPlace) return;
        var g = cv.getContext('2d', { willReadFrequently: true });
        var d;
        try { d = g.getImageData(0, 0, cv.width, cv.height); } catch (e) { return; }
        CardInk.simulateInPlace(d.data, cv.width, cv.height);
        g.putImageData(d, 0, 0);
    }

    /* A background can arrive without going through the file picker: an undo,
     * a restored draft, an opened project, a test. Each of those was a separate
     * place to remember to preload, and forgetting one shows up as a card that
     * silently has no background — so the check lives at the top of the draw
     * instead, where every one of them passes through. */
    function ensureBgImages() {
        ['front', 'back'].forEach(function (w) {
            var b = doc.bg && doc.bg[w];
            if (b && b.src && !images[b.src]) preloadImage(b.src, draw);
        });
    }

    /* ── the veil that makes the text readable, found rather than guessed ────
     * MEASURED, and this is why it exists: across four ordinary photographs ×
     * eight layouts, the contrast check flags SEVEN LAYOUTS OF EIGHT on a plain
     * mid-tone picture at veil 0 — and it is right every time. The company
     * kicker is `accent` at 62 % opacity, which lands at 1.7:1 over mid grey.
     * Moving the handle to ±40 does not clear it; ±80 does.
     *
     * A tool that reports a fault on almost every layout and leaves the reader
     * to find the one control that fixes it has turned a feature into an
     * obstacle course. So the veil is FITTED when a picture is placed: the
     * smallest one that makes every block readable, announced in words, and one
     * undo away.
     *
     * ⚠️ IT SEARCHES THROUGH THE REAL PREFLIGHT, not through a private copy of
     * the contrast rule. A second implementation of "is this readable" would be
     * a second answer, and the one the user is shown would be the other one.
     * The cost is a build + a small bake + a preflight per step; measured at
     * 240 px it is a few milliseconds, and this runs once per uploaded file.
     */
    var VEIL_STEPS = [0, 20, 35, 50, 65, 80, 92, 100];

    function veilProblems(which) {
        var built = buildAll();
        var photos = bakePhotos(built, 240 / (boxes().w + 2 * boxes().bleed));
        var r = Pre.run({
            photos: photos,
            boxes: boxes(), limits: Spec.LIMITS, ink: Ink, measure: measure,
            palette: palette(), doc: doc, sample: { fields: [], touched: false },
            sides: [{ side: which, items: built[which].items, warnings: [] }],
        });
        var err = 0, warn = 0;
        r.issues.forEach(function (i) {
            if (i.code === 'bg_text_unreadable') err++;
            else if (i.code === 'bg_text_thin') warn++;
        });
        return { err: err, warn: warn, score: err * 10 + warn };
    }

    /* Which way to move. A card whose type is mostly paper-coloured needs the
     * picture pushed toward ink, and the other way round; deciding by the
     * PALETTE rather than by trying both halves keeps the search to eight
     * steps instead of sixteen. */
    function veilDirection(which) {
        var items = (lastBuild && lastBuild[which] && lastBuild[which].items) || [];
        var pale = 0, dark = 0;
        var pl = palette();
        items.forEach(function (it) {
            if (it.type !== 'text' || !it.color || !window.CardInk) return;
            var L = CardInk.luminance(CardInk.cmykToRgb(it.color.c, it.color.m, it.color.y, it.color.k));
            if (L > 0.5) pale++; else dark++;
        });
        void pl;
        return pale > dark ? -1 : 1;      // pale type → darken; dark type → lighten
    }

    function fitVeil(which) {
        if (!bgOf(which) || !window.CardPhoto) return null;
        var was = side;
        var savedLight = bgOf(which).light;
        side = which;
        try {
            /* Build first: `veilDirection` reads the last build's text colours,
             * and on the upload path the last build is the one from BEFORE the
             * picture. It happens to give the same answer (a photograph does
             * not change the colour of type) — which is exactly the kind of
             * accident that stops being true after one edit. */
            buildAll();
            var dir = veilDirection(which);
            var best = null;
            for (var i = 0; i < VEIL_STEPS.length; i++) {
                var v = VEIL_STEPS[i] * dir;
                doc.bg[which].light = v;
                var p2 = veilProblems(which);
                if (best === null || p2.score < best.score) best = { light: v, score: p2.score, err: p2.err, warn: p2.warn };
                /* Smallest veil that leaves nothing unreadable wins — stop at
                 * the first one rather than pushing on to zero warnings, which
                 * would flatten the photograph for a shade of comfort. */
                if (p2.err === 0) { best = { light: v, score: p2.score, err: 0, warn: p2.warn }; break; }
            }
            doc.bg[which].light = best ? best.light : savedLight;
            return best;
        } catch (e) {
            doc.bg[which].light = savedLight;
            return null;
        } finally { side = was; }
    }

    function bgOf(which) { return (doc.bg && doc.bg[which]) || null; }
    function setBg(which, patch) {
        if (!doc.bg) doc.bg = { front: null, back: null };
        if (patch === null) { doc.bg[which] = null; return; }
        doc.bg[which] = Object.assign({}, window.CardPhoto ? CardPhoto.DEFAULTS : {},
            doc.bg[which] || {}, patch);
    }

    function logoCtx() {
        if (!doc.logo) return null;
        var img = images[doc.logo.src];
        if (!img || !img.complete || !img.naturalWidth) return null;
        return {
            ok: true, src: doc.logo.src, mono: !!doc.logo.mono,
            ratio: img.naturalWidth / img.naturalHeight,
            natW: img.naturalWidth, natH: img.naturalHeight,
            vector: /^data:image\/svg/.test(doc.logo.src),
            shift: printShift[doc.logo.src] || 0,
        };
    }
    function buildSide(which, qr) {
        return Layout.build(doc, which, {
            boxes: boxes(), palette: palette(), measure: measure, fitter: LL,
            preset: Presets.byId(doc.preset), backPreset: Presets.backById(doc.back),
            qr: qr, logo: logoCtx(), family: doc.fonts.body,
            photo: photoHost(),
        });
    }
    /* ⚠️ THE PERSON SWAP LIVES HERE, not at each call site. Everything that
     * puts a card on screen or in a file goes through this function — the
     * preview, the PNG, the print PDF, the home sheet, the preset thumbnails —
     * and a swap done in only some of them is a tool that shows one employee
     * and exports another. Idempotent: the previewed person is built by merging
     * the list over `doc.data`, so merging it over itself changes nothing. */
    function buildAll() {
        return withPreviewPerson(function () {
            var qr = qrInfo();
            var f = buildSide('front', qr), b = buildSide('back', qr);
            if (doc.scale !== 1) { rescale(f.items); rescale(b.items); }
            lastBuild = { front: f, back: b, qr: qr };
            return lastBuild;
        });
    }
    function rescale(items) {
        for (var i = 0; i < items.length; i++) {
            var it = items[i];
            if (it.type !== 'text') continue;
            it.size *= doc.scale; it.lineHeight *= doc.scale;
        }
    }
    function boundsOf(it) { return Pre.boundsOf(it, measure); }

    /* ── preview ────────────────────────────────────────────────────────────*/
    var viewState = { front: null, back: null };

    /* Canvas will not fetch a webfont on its own: it paints whatever is loaded
       and never comes back. So the faces this text needs are pulled first, and
       the redraw is repeated once they land. */
    var _fontsPending = {};
    function ensurePreviewFaces(built) {
        var PTF = window.PdfTextFont;
        if (!PTF || !PTF.cssPrepare) return;
        var text = '';
        ['front', 'back'].forEach(function (w) {
            ((built && built[w] && built[w].items) || []).forEach(function (it) {
                if (it.type === 'text') text += (it.lines || []).join('');
            });
        });
        if (!text || _fontsPending[text]) return;
        _fontsPending[text] = true;
        Promise.all([PTF.cssPrepare(text, false), PTF.cssPrepare(text, true)])
            .then(function () { draw(); });
    }

    function draw() {
        if (!measure) return;
        ensureBgImages();
        var built = buildAll();
        ensurePreviewFaces(built);
        var B = boxes();
        var showBleed = $('#bc-g-bleed') && $('#bc-g-bleed').checked;
        var pad = showBleed ? B.bleed : 0;

        /* The photo is baked at the size the preview will paint it, which is
         * what makes a blur in millimetres look the same here and on paper. The
         * scale is computed from the FRONT wrapper: both canvases share a
         * column, and re-baking the back at its own rounding would double the
         * work for a difference no eye can see. */
        var wrapW = ($('#bc-wrap-front') && $('#bc-wrap-front').clientWidth) || 380;
        var photos = bakePhotos(built, Math.min(6, wrapW / (B.w + 2 * pad)) * dprNow());

        ['front', 'back'].forEach(function (which) {
            var cv = $('#bc-canvas-' + which);
            var wrap = $('#bc-wrap-' + which);
            if (!cv || !wrap) return;
            var cssW = wrap.clientWidth || 380;
            var totalW = B.w + 2 * pad, totalH = B.h + 2 * pad;
            var scale = cssW / totalW;
            var dpr = Math.min(3, window.devicePixelRatio || 1);
            cv.width = Math.round(cssW * dpr);
            cv.height = Math.round(totalH * scale * dpr);
            cv.style.width = cssW + 'px';
            cv.style.height = Math.round(totalH * scale) + 'px';
            var ctx = cv.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.clearRect(0, 0, cssW, totalH * scale);
            ctx.fillStyle = Render.css(palette().paper);
            ctx.fillRect(0, 0, cssW, totalH * scale);

            ctx.save();
            Render.clipCard(ctx, B, { scale: scale, withBleed: showBleed, radius: doc.radius });
            Render.draw(ctx, built[which].items, {
                scale: scale, originX: pad, originY: pad,
                familyFor: familyFor, images: images, printSim: printSim,
                photos: photos, qrMatrix: matrixFor,
            });
            ctx.restore();

            Render.drawGuides(ctx, { w: B.w, h: B.h, bleed: pad, safe: B.safe }, {
                scale: scale, showBleed: showBleed,
                showTrim: $('#bc-g-trim') && $('#bc-g-trim').checked,
                showSafe: $('#bc-g-safe') && $('#bc-g-safe').checked,
            });

            viewState[which] = { scale: scale, padX: pad, padY: pad };
        });

        /* Preflight grades the card that is ON SCREEN, so the shown employee's
         * telephone is the one checked for a missing digit — the most expensive
         * mistake this tool catches, and the one a roster multiplies by the
         * size of the office. */
        withPreviewPerson(function () { runPreflight(built, photos); });
        saveDraft();
        syncSampleUi();
        syncBgUi();
        /* ⚠️ `renderRoster()`, not `markRosterFields()`. Choosing a person from
         * the picker only calls `draw()`, so marking the form fields from here
         * and leaving the picker's own state to the textarea's `input` event
         * meant the "somebody else is on the card" cue never lit — the one
         * signal that answers «что значит Ветр». The picker and the card are
         * one state; they are refreshed in one place. */
        renderRoster();
        renderSheetNote();
        renderQrStatus(built.qr);
        renderCustomVerdict();
        paintPresetThumbs();
        updateSummaries();
        renderCtx();
        Object.keys(interact).forEach(function (k) { interact[k].refresh(); });
    }

    function matrixFor(payload, ecc) {
        var k = payload + '|' + ecc;
        if (qrCache[k]) return qrCache[k];
        if (!qrLib) return null;
        qrCache[k] = Pdf.qrMatrix(qrLib, payload, ecc);
        return qrCache[k];
    }

    /* ── preflight ──────────────────────────────────────────────────────────*/
    function dprNow() { return Math.min(3, window.devicePixelRatio || 1); }

    function runPreflight(built, photos) {
        var r = Pre.run({
            photos: photos || {},
            boxes: boxes(), limits: Spec.LIMITS, ink: Ink, measure: measure,
            palette: palette(), doc: doc, sample: sampleState(),
            sides: [
                { side: 'front', items: built.front.items, warnings: built.front.warnings },
                { side: 'back', items: built.back.items, warnings: built.back.warnings },
            ],
        });
        /* Errors first, then warnings, then tips: a list ordered by where it
         * happened teaches nothing, a list ordered by what it costs is a work
         * queue. The index survives that sort so the number on the card and the
         * number in the list are the same number. */
        var rank = { error: 0, warn: 1, tip: 2 };
        lastIssues = r.issues.slice().sort(function (a, b) { return rank[a.severity] - rank[b.severity]; });
        lastIssues.forEach(function (i, n) { i.__n = n; i.__title = issueTitle(i); });
        lastPre = r;
        renderPreflight(r);
        return r;
    }

    function renderPreflight(r) {
        var dot = $('#bc-pf-dot'), title = $('#bc-pf-title'), body = $('#bc-pf-body');
        if (!dot || !title || !body) return;
        dot.className = 'bc-pf-dot' + (r.errors ? ' err' : (r.warnings ? ' warn' : ''));
        /* Both counts, when both exist. Showing only the errors — the obvious
         * ternary — hid four warnings behind "ошибок: 2" on a card that had
         * six things wrong with it, and the collapsed panel is the only place
         * a reader learns there is more to open. */
        var bits = [];
        if (r.errors) bits.push(t('pf_errors', { n: r.errors }));
        if (r.warnings) bits.push(t('pf_warnings', { n: r.warnings }));
        if (r.tips) bits.push(t('pf_tips', { n: r.tips }));
        title.textContent = r.clean ? t('pf_ready') : bits.join(' · ');

        if (r.clean) { body.innerHTML = '<p class="bc-pf-ok">' + esc(t('pf_all_good')) + '</p>'; return; }
        var sel = interact[side] && interact[side].selected();
        body.innerHTML = '<div class="bc-pf-list">' + lastIssues.map(function (i) {
            var here = i.id && i.id === sel && i.side === side;
            return '<button type="button" class="bc-pf-item' + (here ? ' on' : '') + '" data-issue="' + i.__n + '">' +
                '<i class="bc-pf-n ' + i.severity + '"></i>' +
                '<span style="color:var(--text-primary);font-weight:600;display:block">' + esc(issueTitle(i)) + '</span>' +
                (issueBody(i) ? '<span style="grid-column:2">' + esc(issueBody(i)) + '</span>' : '') +
                '</button>';
        }).join('') + '</div>';
        $$('.bc-pf-item', body).forEach(function (b) {
            b.addEventListener('click', function () { focusIssue(lastIssues[+b.getAttribute('data-issue')]); });
        });
    }

    /* Clicking an issue takes you to the thing it is about: the right side of
     * the card, the element selected, the marker visible. A warning you cannot
     * act on from where it is written is a warning that gets ignored. */
    function focusIssue(iss) {
        if (!iss) return;
        if (iss.side && iss.side !== side) setSide(iss.side);
        if (iss.id && interact[side]) interact[side].select(iss.id);
        feature('pf_click', iss.code);
        once('pf', function () { stage('checking'); });
    }

    function issueTitle(i) { return t('pf_' + i.code); }
    function issueBody(i) {
        switch (i.code) {
            case 'outside_safe': case 'outside_trim':
                return t('pf_by_mm', { mm: i.mm }) + (i.sample ? ' — «' + trunc(i.sample) + '»' : '');
            case 'no_bleed':   return t('pf_edges', { n: i.edges.length, mm: i.need });
            case 'text_too_small': case 'text_small':
                return t('pf_pt', { pt: i.pt, min: i.min }) + (i.sample ? ' — «' + trunc(i.sample) + '»' : '');
            case 'rich_black_text': return t('pf_pt_only', { pt: i.pt });
            case 'hairline':   return t('pf_stroke', { mm: i.mm, min: i.min });
            case 'ink_over':   return t('pf_tac', { tac: i.tac, max: i.max });
            case 'logo_low_dpi': case 'logo_soft_dpi': return t('pf_dpi', { dpi: i.dpi, want: i.want });
            case 'logo_gamut': return t('pf_gamut', { shift: i.shift });
            case 'qr_unscannable': case 'qr_tight': return t('pf_qr', { mm: i.moduleMm, need: i.needMm });
            case 'low_contrast': case 'contrast_thin':
                return t('pf_contrast', { ratio: i.ratio }) + (i.sample ? ' — «' + trunc(i.sample) + '»' : '');
            case 'overflow':   return i.sample ? '«' + trunc(i.sample) + '»' : '';
            case 'bad_email': case 'bad_site': return '«' + trunc(i.sample) + '»';
            case 'bad_phone':  return '«' + trunc(i.sample) + '» — ' + t('pf_digits', { n: i.n });
            case 'sample_left':
                /* Name the fields, not the count: "3 поля из образца" tells the
                 * reader to go hunting, "Телефон, Почта, Адрес" tells them
                 * exactly what is about to be printed. */
                return (i.fields || []).map(function (f) { return t('f_' + f); }).join(', ');
            case 'bg_low_dpi': case 'bg_soft_dpi': return t('pf_dpi', { dpi: i.dpi, want: i.want });
            case 'bg_text_unreadable': case 'bg_text_thin':
                return t('pf_contrast', { ratio: i.ratio }) + (i.sample ? ' — «' + trunc(i.sample) + '»' : '');
        }
        return '';
    }
    function trunc(s) { s = String(s); return s.length > 40 ? s.slice(0, 38) + '…' : s; }
    function esc(s) {
        return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function renderQrStatus(qr) {
        var el = $('#bc-qr-status');
        if (!el) return;
        if (!qr) { el.textContent = ''; el.className = 'bc-note'; return; }
        var p = qr.plan;
        el.className = 'bc-note ' + (p.level === 'ok' ? 'ok' : (p.level === 'tight' ? 'warn' : 'err'));
        el.textContent = t('qr_status', {
            mm: p.moduleMm, need: p.neededMm || '—',
            rung: t('qr_rung_' + qr.rung), bytes: p.bytes,
        });
    }

    /* ── the contextual toolbar ─────────────────────────────────────────────
     * Only the controls that apply to what is selected, named after it. A
     * global panel of every possible property is how a tool ends up with forty
     * inputs of which two are relevant. */
    function renderCtx() {
        var host = $('#bc-ctx');
        if (!host) return;
        var insp = $('#bc-insp');
        var ctl = interact[side];
        var id = ctl && ctl.selected();
        var off = function () {
            host.className = 'bc-ctx'; host.innerHTML = '';
            if (insp) insp.classList.remove('on');
            syncBgBody();
        };
        if (!id || !lastBuild) { off(); return; }
        var items = lastBuild[side].items, it = null;
        for (var i = 0; i < items.length; i++) if (items[i].id === id) it = items[i];
        if (!it) { off(); return; }

        var over = (doc.overrides[side] || {})[id] || {};
        var parts = ['<span class="bc-ctx-name">' + esc(elementName(it)) + '</span>'];

        if (it.type === 'text') {
            /* An explicit button, not only the gesture. A double-click nobody
             * is told about is a feature nobody uses, and on touch there is no
             * double-click at all.
             * ⚠️ `it.field ||`: this used to be gated on a data field, so the
             * one block a user most wants to change by hand — the monogram —
             * was the one block with no button and no working double-click. */
            if (it.field || it.editable) parts.push(btn('edit', '✎ ' + t('ctx_edit'), t('ctx_edit')));
            /* The monogram only exists BECAUSE there is no logo — the preset
             * swaps the two. That was true in the engine and invisible in the
             * interface: «И вместо этого поместить не текст например а
             * логотип?» is a question the card itself should have answered. */
            if (it.role === 'monogram' && !doc.logo) {
                parts.push(btn('logo-here', '🖼️ ' + t('ctx_use_logo'), t('ctx_use_logo')));
            }
            parts.push(btn('size-', '−A', t('ctx_smaller')));
            parts.push(btn('size+', '＋A', t('ctx_bigger')));
            parts.push(btn('bold', 'B', t('ctx_bold'), (over.weight || it.weight) >= 600));
            ['left', 'center', 'right'].forEach(function (a) {
                parts.push(btn('al:' + a, ({ left: '⇤', center: '↔', right: '⇥' })[a], t('ctx_align'), it.align === a));
            });
            ['ink', 'accent', 'muted', 'paper'].forEach(function (role) {
                parts.push('<button type="button" data-act="col:' + role + '" title="' + esc(t('pal_role_' + role)) +
                    '"><i class="bc-swatch" style="background:' + Render.css(palette()[role]) + ';margin:0"></i></button>');
            });
        } else if (it.type === 'photo') {
            /* The background answers the same two gestures as everything else —
             * drag to move, grip to resize — but "resize" means "zoom the crop"
             * and there is no way to guess that from a corner handle. Buttons
             * that say so, and one that undoes the framing without undoing the
             * picture. */
            parts.push(btn('bg-', '−', t('ctx_smaller')));
            parts.push(btn('bg+', '+', t('ctx_bigger')));
            /* Quarter turns and a mirror — asked for by name («я хочу её
             * повернуть, а функции такой нет»). A phone photograph arrives on
             * its side more often than not, and the only answer used to be
             * "turn it in another program first". */
            parts.push(btn('bg-rotl', '⟲', t('ctx_rotate_l')));
            parts.push(btn('bg-rotr', '⟳', t('ctx_rotate_r')));
            parts.push(btn('bg-flip', '⇋', t('ctx_flip'), !!((bgOf(side) || {}).flip)));
            parts.push(btn('bg-reframe', '↺ ' + t('bg_reframe'), t('bg_reframe')));
        } else if (it.type === 'image') {
            parts.push(btn('lg-', '−', t('ctx_smaller')));
            parts.push(btn('lg+', '+', t('ctx_bigger')));
            /* The logo's other three verbs live in the section on the right;
             * repeated here so the thing you have just selected can be
             * changed where you selected it. Each one drives the section's
             * own control, so there is still one implementation. */
            if (id === 'logo' && doc.logo) {
                parts.push(btn('lg-mono', '◐ ' + t('ctx_mono'), t('logo_mono'), !!doc.logo.mono));
                parts.push(btn('lg-replace', t('ctx_replace'), t('ctx_replace')));
                parts.push(btn('lg-clear', t('logo_clear'), t('logo_clear')));
            }
        } else if (it.type === 'qr') {
            parts.push(btn('qr-', '−', t('ctx_smaller')));
            parts.push(btn('qr+', '+', t('ctx_bigger')));
            parts.push(btn('qr-settings', t('ctx_settings') + '…', t('ctx_settings')));
        }
        parts.push('<span class="sp"></span>');
        var touched = over.dx || over.dy || over.size || over.weight || over.align
            || over.color || typeof over.text === 'string';
        if (touched) parts.push(btn('reset', '↺ ' + t('ctx_reset'), t('ctx_reset')));
        parts.push(btn('deselect', '✕', t('ctx_deselect')));
        parts.push('<span class="bc-ctx-hint">' + esc(t('ctx_hint')) + '</span>');

        host.className = 'bc-ctx on';
        host.innerHTML = parts.join('');
        if (insp) insp.classList.add('on');
        syncBgBody();
        $$('button[data-act]', host).forEach(function (b) {
            b.addEventListener('click', function () { ctxAction(b.getAttribute('data-act'), id, it); });
        });

        function btn(act, label, title, on) {
            return '<button type="button" data-act="' + act + '" title="' + esc(title || '') + '"' +
                (on ? ' class="on"' : '') + '>' + label + '</button>';
        }
    }

    function elementName(it) {
        if (it.type === 'photo') return t('el_bg');
        if (it.type === 'image') return t('el_logo');
        if (it.type === 'qr') return t('el_qr');
        if (it.field) return t('f_' + it.field);
        if (it.role === 'monogram') return t('el_monogram');
        return t('el_text');
    }

    function ctxAction(act, id, it) {
        if (act === 'edit') {
            interact[side] && interact[side].openEditorFor(id);
            feature('ctx', 'edit');
            return;
        }
        if (act === 'deselect') {
            interact[side] && interact[side].select(null);
            return;
        }
        /* Opens the section AND the file dialog: telling somebody where the
         * control lives is half an answer when they are already looking at the
         * thing they want to replace. */
        if (act === 'logo-here') {
            openSection('bc-sec-brand');
            var inp = $('#bc-logo-input');
            if (inp) inp.click();
            feature('ctx', 'logo_here');
            return;
        }
        /* Mirrors of the section's own controls — one implementation each. */
        if (act === 'lg-replace' || act === 'lg-clear' || act === 'lg-mono') {
            var el = $(act === 'lg-replace' ? '#bc-logo-input' : (act === 'lg-clear' ? '#bc-logo-clear' : '#bc-logo-mono'));
            if (el) el.click();
            feature('ctx', act);
            return;
        }
        if (act === 'qr-settings') {
            openSection('bc-sec-qr');
            feature('ctx', act);
            return;
        }
        pushUndo();
        var o = Object.assign({}, (doc.overrides[side] || {})[id] || {});
        if (act === 'bg-rotl' || act === 'bg-rotr' || act === 'bg-flip') {
            var b = bgOf(side) || {};
            if (act === 'bg-flip') {
                setBg(side, { flip: !b.flip });
            } else {
                /* Under a mirror the picture's own clockwise is the viewer's
                 * anticlockwise; the buttons mean what the viewer sees. */
                var step = (act === 'bg-rotr' ? 90 : -90) * (b.flip ? -1 : 1);
                var rot = (b.rot || 0) + step;
                setBg(side, { rot: window.CardPhoto ? CardPhoto.quarterTurn(rot) : ((rot % 360) + 360) % 360 });
            }
            /* The pan was chosen for the previous orientation — a nudge that
             * centred a face in a portrait crop points at nothing once the
             * picture is on its side. The zoom is a size, not a position, and
             * stays. */
            if (o.dx || o.dy) { delete o.dx; delete o.dy; doc.overrides[side][id] = o; }
            setBgRangeUi();
            draw();
            feature('bg', act.slice(3));
            return;
        }
        if (act === 'bg-' || act === 'bg+') {
            o.scale = Math.max(0.5, Math.min(3, (o.scale || 1) + (act === 'bg+' ? 0.1 : -0.1)));
            setOverride(id, o, true);
            setBgRangeUi();
            return;
        }
        if (act === 'bg-reframe') {
            if (doc.overrides[side]) delete doc.overrides[side].bg;
            setBgRangeUi();
            draw();
            return;
        }
        if (act === 'size-') o.size = Math.max(0.5, (o.size || 1) - 0.08);
        else if (act === 'size+') o.size = Math.min(2.5, (o.size || 1) + 0.08);
        else if (act === 'bold') o.weight = ((o.weight || it.weight) >= 600) ? 400 : 700;
        else if (act.indexOf('al:') === 0) o.align = act.slice(3);
        else if (act.indexOf('col:') === 0) o.color = act.slice(4);
        else if (act === 'lg-') o.scale = Math.max(0.25, (o.scale || 1) - 0.1);
        else if (act === 'lg+') o.scale = logoGrow(o.scale || 1, it, id);
        else if (act === 'qr-') { setQrSize(doc.qr.size - 1.5); return; }
        else if (act === 'qr+') { setQrSize(doc.qr.size + 1.5); return; }
        else if (act === 'reset') o = {};
        setOverride(id, o, true);
        once('manip', function () { stage('refining'); });
        feature('ctx', act);
    }

    /* The ceiling on a logo is the CARD, not a round number.
     *
     * It used to be a flat `Math.min(4, ...)` on the multiplier, which is blind
     * to the shape it multiplies: a square mark at 4x is 20 mm and fine, while
     * a 3:1 wordmark at 4x is 96 mm on a 90 mm card — measured 2026-08-23, it
     * hung 10 mm off the left edge. The preview CLIPS at the card, so past that
     * point the person is enlarging something they cannot see, and nothing said
     * a word.
     *
     * ⚠️ Clamping the WIDTH is not enough, and that was measured too: the first
     * attempt capped the scale so the logo could not be wider than the bleed
     * box, and it still ran off — because the layout anchors this mark by its
     * right edge, so growing moves the ORIGIN leftward. What has to fit is the
     * resulting BOX, and the only thing that knows where the box lands is the
     * layout itself.
     *
     * So: build it and look. A trial build with the candidate scale, the real
     * geometry read off the real item, and a short bisection to stop exactly at
     * the edge rather than one step short of it. Exact for any anchoring rule,
     * including ones written later.
     */
    function logoFits(id, scale) {
        var over = doc.overrides[side] || (doc.overrides[side] = {});
        var had = Object.prototype.hasOwnProperty.call(over, id);
        var saved = over[id];
        over[id] = Object.assign({}, saved || {}, { scale: scale });
        var probe = null;
        try {
            var built = buildSide(side, lastBuild ? lastBuild.qr : null);
            for (var i = 0; i < built.items.length; i++) {
                if (built.items[i].id === id) { probe = built.items[i]; break; }
            }
        } catch (e) { probe = null; }
        if (had) over[id] = saved; else delete over[id];
        if (!probe) return true;      // nothing to judge — do not block the user
        var B = boxes(), e = 0.01;
        return probe.x >= -B.bleed - e && probe.y >= -B.bleed - e
            && probe.x + probe.w <= B.w + B.bleed + e
            && probe.y + probe.h <= B.h + B.bleed + e;
    }

    function logoGrow(cur, it, id) {
        var want = Math.min(4, cur + 0.1);
        if (want <= cur) return cur;
        if (logoFits(id, want)) return want;
        /* Land on the edge instead of refusing the click: eight halvings put
         * the logo within ~0.4 % of the largest size that still prints. */
        var lo = cur, hi = want;
        for (var i = 0; i < 8; i++) {
            var mid = (lo + hi) / 2;
            if (logoFits(id, mid)) lo = mid; else hi = mid;
        }
        return Math.round(lo * 1000) / 1000;
    }

    /* One section at a time (see bindSections): opening one by name is how a
     * control elsewhere on the page — the selection toolbar, a preflight
     * item — sends the reader to the right place without the page jumping. */
    function openSection(id) {
        var sec = $('#' + id);
        if (!sec) return;
        sec.open = true;
        sec.scrollIntoView({ block: 'nearest' });
    }
    function selectedId() {
        var c = interact[side];
        return c ? c.selected() : null;
    }
    /* On a phone the card and the sections are one column, and the inspector
     * opens where it was asked for. Opened by a control in a section, it goes
     * directly under that control's row: under the card it would open off
     * screen, far above the control, and the press would look like it did
     * nothing. After the whole section is not close enough either — measured
     * at 360×640, the logo block below the door put it at 698 px. Pressed from
     * inside the inspector (Replace picture), it stays where it is; closed, or
     * opened from anything else (a tap on the card, a preflight item), it is
     * back under the card. The desktop keeps it in the card's column. */
    var PHONE = window.matchMedia ? window.matchMedia('(max-width: 939px)') : null;
    var inspHome = null, lastPress = null;
    function placeInspector() {
        var insp = $('#bc-insp');
        if (!insp || !insp.parentNode) return;
        if (!inspHome) {
            inspHome = document.createComment('bc-insp');
            insp.parentNode.insertBefore(inspHome, insp);
        }
        var anchor = inspHome;
        if (insp.classList.contains('on') && PHONE && PHONE.matches && lastPress && lastPress.closest) {
            if (insp.contains(lastPress)) return;
            var sec = lastPress.closest('details.bc-sec');
            if (sec) anchor = lastPress.closest('.bc-more') || lastPress.closest('.bc-sub') || sec;
        }
        if (insp.previousSibling !== anchor) anchor.after(insp);
    }
    /* The photograph's panel follows the SELECTION, not the existence of a
     * photograph: it is the inspector of the thing under the cursor, and it
     * used to be a section that stayed open on its own while somebody was
     * doing something else entirely. */
    function syncBgBody() {
        var body = $('#bc-bg-body');
        if (!body) return;
        body.hidden = !(bgOf(side) && selectedId() === 'bg');
    }

    function setOverride(id, patch, replace) {
        if (!doc.overrides[side]) doc.overrides[side] = {};
        doc.overrides[side][id] = replace ? patch
            : Object.assign({}, doc.overrides[side][id] || {}, patch);
        draw();
    }
    function setQrSize(v) {
        doc.qr.size = Math.max(8, Math.min(40, Math.round(v * 2) / 2));
        var r = $('#bc-qr-size'), o = $('#bc-qr-size-out');
        if (r) r.value = doc.qr.size;
        if (o) o.textContent = doc.qr.size;
        qrCache = {};
        draw();
    }

    /* ── section summaries ──────────────────────────────────────────────────
     * A collapsed section must still say what is inside it, or it is a tab with
     * extra steps. */
    function updateSummaries() {
        set('#bc-sum-data', [doc.data.name, doc.data.role].filter(Boolean).join(' · ') || t('sum_empty'));
        set('#bc-sum-look', [t('preset_' + doc.preset), t('pal_' + doc.palette)].filter(Boolean).join(' · '));
        set('#bc-sum-qr', doc.qr.enabled
            ? t('sum_qr_on', { mm: doc.qr.size, what: t('qr_mode_' + doc.qr.mode) })
            : t('sum_qr_off'));
        var brand = [];
        if (bgOf('front') || bgOf('back')) brand.push(t('sum_bg_on'));
        if (doc.logo) brand.push(t('sum_logo_on'));
        set('#bc-sum-brand', brand.length ? brand.join(' · ') : t('sum_no_logo'));
        var f = fmt(), B = boxes();
        set('#bc-sum-print', round1(B.w) + '×' + round1(B.h) + ' мм · ' + t('sum_bleed', { mm: round1(B.bleed) }));
        var team = rosterPeople().length;
        set('#bc-sum-team', team ? t('roster_count', { n: team }) : t('sum_team_off'));
        function set(sel, v) { var e = $(sel); if (e) e.textContent = v; }
    }
    function round1(v) { return Math.round(v * 10) / 10; }

    /* ── preset thumbnails, rendered with THEIR data ────────────────────────*/
    function paintPresetThumbs() {
        $$('.bc-preset-thumb').forEach(function (cv) {
            var id = cv.getAttribute('data-preset');
            var B = boxes();
            var w = cv.clientWidth || 104;
            var scale = w / B.w;
            var dpr = Math.min(2, window.devicePixelRatio || 1);
            cv.width = Math.round(w * dpr);
            cv.height = Math.round(B.h * scale * dpr);
            cv.style.height = Math.round(B.h * scale) + 'px';
            var ctx = cv.getContext('2d');
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
            ctx.fillStyle = Render.css(palette().paper);
            ctx.fillRect(0, 0, w, B.h * scale);
            var built = Layout.build(doc, 'front', {
                boxes: B, palette: palette(), measure: measure, fitter: LL,
                preset: Presets.byId(id), backPreset: Presets.backById(doc.back),
                qr: null, logo: logoCtx(), family: doc.fonts.body,
            });
            if (doc.scale !== 1) rescale(built.items);
            ctx.save();
            Render.clipCard(ctx, B, { scale: scale, withBleed: false, radius: doc.radius });
            Render.draw(ctx, built.items, {
                scale: scale, originX: 0, originY: 0,
                familyFor: familyFor, images: images, printSim: printSim, qrMatrix: function () { return null; },
            });
            ctx.restore();
        });
    }

    /* ── export ─────────────────────────────────────────────────────────────*/
    function exportOpts(extra) {
        return Object.assign({
            text: $('#bc-text-mode') && $('#bc-text-mode').value === 'font' ? 'font' : 'curves',
            marks: !!($('#bc-marks') && $('#bc-marks').checked),
            headingFace: doc.fonts.heading, bodyFace: doc.fonts.body,
            logoMono: !!(doc.logo && doc.logo.mono),
            title: (doc.data.name || doc.data.company || 'card'),
            rulerCaption: t('ruler_caption'),
        }, extra || {});
    }
    function cardKey(data) {
        /* `data` lets the batch price ONE PERSON with the same key the single
         * export would produce for them — so the same person on another day is
         * free either way, and a list of twelve is twelve, not one. */
        var s = JSON.stringify([doc.format, doc.portrait, doc.preset, doc.back, doc.palette,
            doc.fonts, doc.scale, doc.radius, data || doc.data, doc.qr, doc.overrides,
            doc.logo && doc.logo.src ? doc.logo.src.length : 0]);
        var h = 5381;
        for (var i = 0; i < s.length; i++) h = ((h * 33) ^ s.charCodeAt(i)) >>> 0;
        return 'card:' + h.toString(36);
    }
    /* ⚠️ AN EXPORT ALWAYS ENDS, AND THE BUTTON SAYS WHAT IS HAPPENING. Every
     * wait inside it is bounded — the PDF libraries through the asset chain,
     * the faces through PdfTextFont and cardFontsReady(), the canvas through
     * window.__canvasBlob — and the button that started it reads «Готовлю
     * файл…» until it does. Tier-0, 23–25.09.2026: three exports sat 61–690 s
     * in «exporting» with nothing on screen, all three for people without
     * cookie consent (0 of 254 exports for those with it), and 23 sessions
     * pressed the button again while the first export was still running. The
     * day's unit is spent only when the browser took the file: a Promise from
     * __downloadFile is not a verdict until it is awaited.
     * Guard: tests/playwright/business-card-export-never-hangs.mjs. */
    function withLimit(fn, sels) {
        var btns = busyButtons(sels);
        if (btns.some(function (b) { return b.getAttribute('aria-busy') === 'true'; })) return;
        var run = window.__toolLimit ? window.__toolLimit.begin(SLUG, [cardKey()]) : { commit: noop, abort: noop };
        if (!run) return;
        var release = busy(btns);
        Promise.resolve().then(fn).then(function (saved) {
            if (saved === false) { run.abort(); stage('ready'); return; }
            run.commit();
        }).catch(function (e) {
            run.abort();
            failed(e);
        }).then(release, release);
    }
    /* A failed export is the single most important thing that can happen
     * here. A wait that went silent names its phase; the person reads what did
     * not arrive instead of a generic error — and the stage says it too
     * (tier-0 beacon, seen without consent). */
    function failed(e) {
        var phase = e && e.phase;
        stage('error', { reason: phase || String((e && e.message) || e).slice(0, 120) });
        var key = (phase === 'fonts' || phase === 'font_file') ? 'err_fonts'
            : phase === 'script' ? 'err_script'
                : phase === 'encode' ? 'err_encode' : null;
        if (key && window.__toast) { window.__toast(t(key), 'error'); return; }
        window.__handleError && window.__handleError(e, SLUG);
    }
    function busyButtons(sels) {
        return (sels || []).map(function (s) { return $(s); }).filter(Boolean);
    }
    function busy(btns) {
        var kept = btns.map(function (b) { return { b: b, html: b.innerHTML, off: b.disabled }; });
        kept.forEach(function (k) { k.b.setAttribute('aria-busy', 'true'); k.b.disabled = true; k.b.textContent = t('preparing'); });
        return function () {
            kept.forEach(function (k) { k.b.removeAttribute('aria-busy'); k.b.disabled = k.off; k.b.innerHTML = k.html; });
        };
    }
    /* The browser's verdict on the file, not the call: false = it did not take it. */
    function deliver(blob, filename) {
        return Promise.resolve(window.__downloadFile(blob, filename)).then(function (r) { return r !== false; });
    }
    /* The faces the canvas draws with, loaded before the PNG is drawn — a
     * face still in flight would print in the fallback. Bounded: a request
     * that never answers is a named failure, not an export waiting for ever. */
    function cardFontsReady() {
        if (!document.fonts || !document.fonts.load) return Promise.resolve();
        var jobs = [];
        [faceCss(doc.fonts.heading), faceCss(doc.fonts.body)].forEach(function (f) {
            jobs.push(document.fonts.load('400 16px "' + f + '"'), document.fonts.load('700 16px "' + f + '"'));
        });
        return window.__bounded(Promise.all(jobs), 45000, 'fonts').catch(function (e) {
            if (window.__isWaitTimeout(e)) throw e;
            var err = new Error('card face failed: ' + ((e && e.message) || e));
            err.phase = 'fonts';
            throw err;
        });
    }
    function noop() {}
    /* ── the shown employee, in place, for the length of one call ────────────
     * `doc.data` is what gets saved, what the draft restores and what the form
     * writes into; the previewed employee is a derived object that must never
     * become any of those. So it is swapped in for exactly as long as the thing
     * that needs it runs, and out again before anything persists.
     *
     * Everything that draws or exports goes through it, because a preview that
     * shows one employee and a button that exports another is worse than no
     * preview at all. */
    function withPreviewPerson(fn) {
        var person = previewData();
        if (!person || person === doc.data) return fn();
        var own = doc.data;
        doc.data = person;
        try { return fn(); } finally { doc.data = own; }
    }

    function sidesForExport() {
        var built = buildAll();
        var out = [{ side: 'front', items: built.front.items }];
        if (doc.back !== 'blank') out.push({ side: 'back', items: built.back.items });
        return out;
    }
    /* ── several people, one design ──────────────────────────────────────────
     *
     * The real order is not "a business card", it is "cards for the twelve
     * people in the office". Doing that here today means filling the form
     * twelve times and keeping track of twelve downloads; every paid competitor
     * charges for the alternative.
     *
     * The list is parsed by `CertData.parseList` — the certificate maker's,
     * already carrying delimiter detection, header detection and RFC-4180
     * quoting. A second list parser in the same repository would be a second
     * set of bugs about semicolons.
     *
     * ⚠️ COLUMNS ARE MATCHED BY HEADER WHEN THERE IS ONE, by position when
     * there is not — and the positional order is stated in the UI rather than
     * guessed at, because "почему телефон попал в должность" is the entire
     * failure mode of this feature.
     *
     * Fields the list does not carry (company, site, address) come from the
     * form: they are the same for everyone, which is the whole point. */
    var ROSTER_MAX = 200;
    var ROSTER_POS = ['name', 'role', 'phone', 'email'];
    var ROSTER_SYNONYMS = {
        name: ['фио', 'имя', 'name', 'full name', 'fullname', 'сотрудник', 'ф.и.о.', 'ф. и. о.'],
        role: ['должность', 'роль', 'role', 'position', 'title', 'job', 'должность/роль'],
        phone: ['телефон', 'тел', 'phone', 'mobile', 'моб', 'номер'],
        phone2: ['телефон 2', 'тел2', 'phone 2', 'phone2', 'доб', 'ext'],
        email: ['почта', 'email', 'e-mail', 'mail', 'эл. почта'],
        site: ['сайт', 'site', 'website', 'url'],
        company: ['компания', 'company', 'организация', 'org'],
        tagline: ['слоган', 'tagline', 'подпись', 'девиз'],
        address: ['адрес', 'address'],
        telegram: ['telegram', 'телеграм', 'тг'],
        whatsapp: ['whatsapp', 'вотсап', 'ватсап'],
    };

    function normHeader(h) {
        return String(h || '').toLowerCase().replace(/[\s._]+/g, ' ').trim();
    }
    function fieldForHeader(h) {
        var n = normHeader(h);
        for (var f in ROSTER_SYNONYMS) {
            if (ROSTER_SYNONYMS[f].indexOf(n) >= 0) return f;
        }
        return null;
    }

    /** The roster as the export will see it: one data object per person. */
    function rosterPeople() {
        var el = $('#bc-roster');
        var text = el ? el.value : '';
        if (!text || !text.trim()) return [];
        if (!window.CertData || !CertData.parseList) return [];
        var parsed = CertData.parseList(text, {});
        var map = null;
        if (parsed.headers) {
            map = parsed.headers.map(fieldForHeader);
            /* A header row nobody recognised is data, not labels: putting
             * "Иванов" in a column called "Иванов" loses a person. */
            if (!map.some(Boolean)) { parsed.rows.unshift(parsed.headers); map = null; }
        }
        var out = [];
        parsed.rows.slice(0, ROSTER_MAX).forEach(function (row) {
            if (!row.length || !row.join('').trim()) return;
            var d = Object.assign({}, doc.data);
            row.forEach(function (cell, k) {
                var f = map ? map[k] : ROSTER_POS[k];
                if (f && String(cell).trim() !== '') d[f] = String(cell).trim();
            });
            /* A row with no name is a stray blank line, not a person. */
            if (!String(d.name || '').trim()) return;
            out.push(d);
        });
        return out;
    }

    /** Build both sides for one person's data without disturbing the document. */
    function sidesFor(data) {
        var saved = doc.data;
        doc.data = data;
        try {
            var qr = qrInfo();
            var f = buildSide('front', qr), b = buildSide('back', qr);
            if (doc.scale !== 1) { rescale(f.items); rescale(b.items); }
            var sides = [{ side: 'front', items: f.items }];
            if (doc.back !== 'blank') sides.push({ side: 'back', items: b.items });
            return sides;
        } finally { doc.data = saved; }
    }

    /* ── whose card is on screen ─────────────────────────────────────────────
     * Pasting a list of employees used to change nothing you could see: the
     * roster fed the batch export and the preview kept showing the form. The
     * complaint — «я добавляю сотрудников а они не добавляются сразу в
     * визитку» — is not really about the preview, it is that a list you cannot
     * SEE is a list you cannot check, and the whole promise of this tool is
     * that you check the card before the printer does.
     *
     * The index, not the person: a roster is retyped constantly and an index
     * survives an edit that a name does not. */
    /* ⚠️⚠️ −1 IS "MY OWN CARD", AND IT IS THE STARTING VALUE. It was 0, and
     * that shipped a defect worse than the one the preview was built to fix.
     *
     * The roster textarea is remembered in localStorage (`__toolSettings.bind`),
     * so a word typed into it days ago comes back on the next visit. With the
     * preview keyed to person 0, that word BECAME THE NAME ON THE CARD —
     * silently, from a section that is collapsed by default, over the top of
     * the name in the form. Reported the day it shipped, as «что значит Ветр»:
     * a stray five letters had taken over the whole document and there was
     * nowhere on screen that said why.
     *
     * The rule now separates intent from memory:
     *   TYPING a list is intent          → the card switches to the first
     *                                      person, which is the whole point of
     *                                      the preview;
     *   a REMEMBERED list is not intent  → the card stays the user's own, and
     *                                      the picker merely offers the list.
     *
     * And the way back is always in the picker, not only in the delete key:
     * "Моя карточка" is option one, permanently. */
    var PERSON_OWN = -1;
    var personIdx = PERSON_OWN;
    /* Seeded from the RESTORED list, so a remembered roster counts as "already
     * there" and never trips the transition below. */
    var rosterHadPeople = false;

    function previewData() {
        if (personIdx < 0) return null;
        var ppl = rosterPeople();
        if (!ppl.length) return null;
        if (personIdx >= ppl.length) personIdx = 0;
        return ppl[personIdx];
    }
    /* ── typing into a field the list already answers ────────────────────────
     * `doc.data` is the BASE every person is merged onto, so the keystroke is
     * always kept — it is what a row without that column will use. But while an
     * employee who HAS that column is on screen, nothing visible happens, and
     * an interface that accepts input and shows no result has taught the user
     * that it is broken.
     *
     * So: keep the value, say why the card did not move, and say it ONCE. A
     * toast per keystroke is not an explanation, it is weather. */
    var _rosterToldFor = {};
    function noteRosterOwned(f) {
        if (!rosterOwnedFields()[f] || _rosterToldFor[f]) return;
        _rosterToldFor[f] = true;
        window.__toast && window.__toast(t('roster_owns_field', { f: t('f_' + f) }), 'info');
    }

    /* Which fields the shown employee takes from the list rather than from the
     * form. */
    function rosterOwnedFields() {
        var d = previewData();
        if (!d) return {};
        var owned = {};
        FIELDS.forEach(function (f) { if (d[f] !== doc.data[f]) owned[f] = true; });
        return owned;
    }

    /* The label carries the fact, the toast only confirms it. A field whose
     * value is coming from the list says so where the value is, which is the
     * one place somebody is looking when they wonder why it will not change. */
    function markRosterFields() {
        var owned = rosterOwnedFields();
        FIELDS.forEach(function (f) {
            var el = $('#bc-f-' + f);
            if (!el) return;
            var lab = el.parentNode && el.parentNode.querySelector('label');
            if (!lab) return;
            if (lab.__bcBase == null) lab.__bcBase = lab.textContent;
            var want = owned[f] ? (lab.__bcBase + ' · ' + t('from_roster')) : lab.__bcBase;
            if (lab.textContent !== want) lab.textContent = want;
            el.classList.toggle('bc-owned', !!owned[f]);
        });
    }

    function renderPeoplePicker(people) {
        var wrap = $('#bc-who'), sel = $('#bc-person');
        if (!wrap || !sel) return;
        wrap.hidden = people.length < 1;
        var main = $('#bc-export-batch-main');
        if (main) {
            main.hidden = people.length < 1;
            if (people.length) main.textContent = t('dl_batch_n', { n: people.length });
        }
        markRosterFields();
        if (!people.length) { personIdx = PERSON_OWN; return; }
        if (personIdx >= people.length) personIdx = 0;
        /* "Моя карточка" first, always. A picker whose only options are other
         * people is a trap you can enter and not leave. */
        var want = t('who_own') + '\u0000'
            + people.map(function (d, i) { return (i + 1) + '. ' + d.name; }).join('\u0000');
        if (sel.__bcKey !== want) {
            sel.__bcKey = want;
            sel.innerHTML = '<option value="-1">' + esc(t('who_own')) + '</option>'
                + people.map(function (d, i) {
                    return '<option value="' + i + '">' + esc((i + 1) + '. ' + d.name) + '</option>';
                }).join('');
        }
        sel.value = String(personIdx);
        wrap.classList.toggle('on', personIdx >= 0);
        var note = $('#bc-who-note');
        /* Two different sentences, because they answer two different questions.
         * Showing a person: "why is this name here and where do I change it".
         * Showing your own card: "there is a list, it is not on screen". */
        if (note) note.textContent = personIdx >= 0 ? t('who_note') : t('who_note_own', { n: people.length });
    }

    function renderRoster() {
        var out = $('#bc-roster-out');
        if (!out) return;
        var people = rosterPeople();
        renderPeoplePicker(people);
        var btn = $('#bc-export-batch');
        if (btn) btn.disabled = people.length < 1;
        if (!people.length) {
            out.textContent = ($('#bc-roster') && $('#bc-roster').value.trim())
                ? t('roster_none') : '';
            out.className = 'bc-note' + (($('#bc-roster') && $('#bc-roster').value.trim()) ? ' warn' : '');
            return;
        }
        out.className = 'bc-note ok';
        var names = people.slice(0, 5).map(function (d) { return d.name; }).join(', ');
        out.textContent = t('roster_count', { n: people.length })
            + ' — ' + names + (people.length > 5 ? ' …' : '');
    }

    function exportBatch() {
        var people = rosterPeople();
        if (!people.length) return;
        /* ⚠️ THE UNIT IS A PERSON, NOT A PRESS OF THE BUTTON. Twelve people are
         * twelve deliverables; charging one would make the daily cap a
         * formality anybody could step around by pasting a list. Repeating the
         * same person on another day is still free — the key is their content,
         * exactly like the single-card key. */
        var keys = people.map(function (d) { return cardKey(d); });

        /* ⚠️ NO CUSTOM REFUSAL HERE. `begin()` already distinguishes "the day is
         * spent" from "there is budget left, your queue is bigger than it" and
         * renders the second through the note's `[data-tl-body-over]` slot. The
         * tool's job is to FILL that slot (`limit_body_over`, nine locales) —
         * a toast of my own would be a second message saying the same thing in
         * a place that disappears. */
        var btns = busyButtons(['#bc-export-batch', '#bc-export-batch-main']);
        if (btns.some(function (b) { return b.getAttribute('aria-busy') === 'true'; })) return;
        var L = window.__toolLimit;
        var run = L ? L.begin(SLUG, keys) : { commit: noop, abort: noop };
        if (!run) return;
        var release = busy(btns);
        stage('exporting', { kind: 'batch', n: people.length });
        Promise.resolve().then(function () {
            return Pdf.batchPdf({
                boxes: boxes(),
                people: people.map(function (d) { return { sides: sidesFor(d) }; }),
                opts: exportOpts(),
            });
        }).then(function (b) {
            return save(b, 'application/pdf', 'batch').then(function (ok) {
                if (!ok) { run.abort(); stage('ready'); return; }
                feature('batch', String(people.length));
                window.__toast && window.__toast(t('roster_done', { n: people.length }), 'success');
                run.commit();
            });
        }).catch(function (e) {
            run.abort();
            failed(e);
        }).then(function () { release(); renderRoster(); }, function () { release(); renderRoster(); });
    }

    /* What the A4 sheet will contain, in words, next to the button that makes
     * it. This is not decoration: the imposition turned the card 90° to fit
     * more of them on the page and the exporter drew them upright, so for a
     * month the button produced twelve overlapping cards and the page said
     * nothing at all about what was coming. A number that has to match the
     * paper is a number that should be on screen. */
    function renderSheetNote() {
        var el = $('#bc-sheet-note');
        if (!el) return;
        var imp = sheetImposition();
        if (!imp || !imp.perSheet) { el.textContent = t('sheet_none'); el.className = 'bc-note warn'; return; }
        el.className = 'bc-note';
        el.textContent = t('sheet_note', {
            n: imp.perSheet, cols: imp.cols, rows: imp.rows,
            sheet: ($('#bc-sheet-size') && $('#bc-sheet-size').value === 'letter') ? 'Letter' : 'A4',
        }) + (imp.rotated ? ' · ' + t('sheet_turned') : '');
    }

    function sheetImposition() {
        var sheetId = ($('#bc-sheet-size') && $('#bc-sheet-size').value) || 'a4';
        return Spec.imposition(fmt(), {
            bleed: doc.bleed, safe: doc.safe, portrait: doc.portrait,
            bleeds: !!($('#bc-sheet-bleeds') && $('#bc-sheet-bleeds').checked),
            sheet: sheetId === 'letter' ? { w: 215.9, h: 279.4 } : { w: 210, h: 297 },
        });
    }

    function exportPdf() {
        withLimit(function () {
            stage('exporting', { kind: 'pdf' });
            var sides = withPreviewPerson(sidesForExport);
            return Pdf.cardPdf({ boxes: boxes(), sides: sides, opts: exportOpts() })
                .then(function (b) { return save(b, 'application/pdf', 'pdf'); });
        }, ['#bc-export-pdf']);
    }
    function exportSheet() {
        withLimit(function () {
            stage('exporting', { kind: 'sheet' });
            var bleeds = !!($('#bc-sheet-bleeds') && $('#bc-sheet-bleeds').checked);
            var sheetId = ($('#bc-sheet-size') && $('#bc-sheet-size').value) || 'a4';
            var sheet = sheetId === 'letter' ? { w: 215.9, h: 279.4 } : { w: 210, h: 297 };
            var imp = Spec.imposition(fmt(), {
                bleed: doc.bleed, safe: doc.safe, portrait: doc.portrait, bleeds: bleeds, sheet: sheet,
            });
            if (!imp.perSheet) throw new Error(t('err_no_fit'));
            return Pdf.sheetPdf({
                boxes: boxes(), sides: withPreviewPerson(sidesForExport), imposition: imp,
                opts: exportOpts({ bleeds: bleeds, marks: false }),
            }).then(function (b) {
                return save(b, 'application/pdf', 'sheet').then(function (ok) {
                    if (ok) window.__toast && window.__toast(t('sheet_done', { n: imp.perSheet }), 'success');
                    return ok;
                });
            });
        }, ['#bc-export-sheet']);
    }
    function exportPng() {
        withLimit(function () {
            stage('exporting', { kind: 'png' });
            return cardFontsReady().then(drawPng).then(function (cv) {
                return window.__canvasBlob(cv, 'image/png');
            }).then(function (b) {
                return deliver(b, name('png')).then(function (ok) {
                    if (ok) reportExport('png', b.size);
                    return ok;
                });
            });
        }, ['#bc-export-png']);
    }
    function drawPng() {
        var B = boxes();
        var withBleed = !!($('#bc-png-bleed') && $('#bc-png-bleed').checked);
        var dpi = parseInt(($('#bc-png-dpi') && $('#bc-png-dpi').value) || '300', 10);
        var pad = withBleed ? B.bleed : 0, scale = dpi / 25.4;
        var built = withPreviewPerson(buildAll);
        /* ⚠️ Baked AT THE EXPORT SCALE, not reused from the preview. The
         * preview's canvas is ~380 px wide; handing it to a 300 dpi PNG
         * would ship a 1063 px card whose background is a four-times
         * upscale of a thumbnail — and it would look perfectly fine on the
         * screen it was made on. */
        var photos = bakePhotos(built, scale);
        var cv = document.createElement('canvas');
        cv.width = Math.round((B.w + 2 * pad) * scale);
        cv.height = Math.round((B.h + 2 * pad) * scale);
        var ctx = cv.getContext('2d');
        ctx.fillStyle = Render.css(palette().paper);
        ctx.fillRect(0, 0, cv.width, cv.height);
        ctx.save();
        Render.clipCard(ctx, B, { scale: scale, withBleed: withBleed, radius: doc.radius });
        Render.draw(ctx, built[side].items, {
            scale: scale, originX: pad, originY: pad,
            familyFor: familyFor, images: images, printSim: printSim,
            photos: photos, qrMatrix: matrixFor,
        });
        ctx.restore();
        return cv;
    }
    function save(bytes, mime, kind) {
        return deliver(new Blob([bytes], { type: mime }), name(kind === 'sheet' ? 'sheet.pdf' : 'pdf')).then(function (ok) {
            if (ok) reportExport(kind, bytes && bytes.length);
            return ok;
        });
    }

    /* One row per delivered file. `__aeToolComplete` also closes the stage
     * machine, so dwell and abandon land correctly. */
    function reportExport(kind, bytes) {
        var shape = exportShape(kind);
        shape.bytes = bytes || null;
        window.__aeToolComplete && window.__aeToolComplete(shape);

        /* Two things worth knowing separately, because they mean the tool did
         * not do its job rather than that the user made a choice. */
        if (shape.errors) {
            diagnostic({ issue: 'exported_with_errors', errors: shape.errors,
                codes: (lastIssues || []).filter(function (i) { return i.severity === 'error'; })
                    .map(function (i) { return i.code; }).slice(0, 6).join(','), kind: kind });
        }
        if (shape.has_qr && (shape.qr_level === 'unscannable' || shape.qr_level === 'too_big')) {
            diagnostic({ issue: 'qr_unscannable_exported', mode: shape.qr_mode, kind: kind });
        }
    }
    function name(ext) {
        var who = doc.data.name || doc.data.company || t('card');
        var base = window.__outputName ? window.__outputName.sanitize(who)
            : String(who).replace(/[^\p{L}\p{N}\-_ ]+/gu, '').trim();
        return (base || 'card') + '-' + fmt().id + '.' + ext.replace(/^.*\./, '');
    }
    /* ── telemetry ───────────────────────────────────────────────────────────
     * Instrumented around the questions that would CHANGE something, not
     * "everything, just in case":
     *
     *   1. Was the direct-manipulation redesign right? The tool was rebuilt on
     *      the belief that people want to touch the card. `refining` and the
     *      `manip` events answer that; `overrides` in the export payload says
     *      whether the adjustments survived to the deliverable.
     *   2. Does the preflight land? `errors`/`warnings` are recorded AT EXPORT.
     *      Somebody exporting with two errors means the panel is decoration.
     *   3. Which formats, presets and palettes are real? Ten formats and twelve
     *      layouts is a lot to maintain if three of each carry the traffic.
     *   4. Is the QR ladder working? `qr_level` distribution, plus a diagnostic
     *      when an unreadable code reaches the PDF anyway.
     *
     * Stages are a LINE (ready → editing → styling → refining → complete), so
     * dwell and abandon land on a phase rather than on a soup of clicks. Each
     * stage fires once; `once()` is what keeps a 40-keystroke session from
     * emitting 40 events.
     */
    var _fired = {};
    function once(key, fn) {
        if (_fired[key]) return;
        _fired[key] = 1;
        try { fn(); } catch (_) { }
    }
    function stage(n, meta) { window.__stage && window.__stage(n, meta); }
    function feature(k, v, extra) {
        var d = extra || {};
        d.value = v;
        window.__aeFunnel && window.__aeFunnel(k, d);
    }
    function diagnostic(d) { window.__aeDiagnostic && window.__aeDiagnostic(d); }

    /* What the session produced, in one payload. Everything here is a property
     * of the DELIVERABLE, so a single row explains what someone actually made. */
    function exportShape(kind) {
        var pf = null;
        try { pf = lastPre; } catch (_) { }
        var ov = (doc.overrides.front || {}), ob = (doc.overrides.back || {});
        var qr = lastBuild && lastBuild.qr;
        return {
            kind: kind,
            format: doc.format,
            portrait: !!doc.portrait,
            preset: doc.preset,
            back: doc.back,
            palette: doc.palette === 'custom' ? 'custom' : doc.palette,
            font_h: doc.fonts.heading,
            font_b: doc.fonts.body,
            radius: doc.radius,
            text_mode: ($('#bc-text-mode') || {}).value || 'curves',
            marks: !!($('#bc-marks') || {}).checked,
            has_logo: !!doc.logo,
            logo_mono: !!(doc.logo && doc.logo.mono),
            has_qr: !!doc.qr.enabled,
            qr_mode: doc.qr.enabled ? doc.qr.mode : null,
            qr_level: qr && qr.plan ? qr.plan.level : null,
            qr_rung: qr ? qr.rung : null,
            /* The two numbers that say whether the preflight is doing its job. */
            errors: pf ? pf.errors : null,
            warnings: pf ? pf.warnings : null,
            /* …and the one that says whether the redesign is used. */
            overrides: Object.keys(ov).length + Object.keys(ob).length,
            fields: FIELDS.filter(function (f) { return (doc.data[f] || '').trim(); }).length,
        };
    }

    /* ── project file ───────────────────────────────────────────────────────*/
    function saveProject() {
        window.__downloadFile(new Blob([JSON.stringify(doc, null, 1)], { type: 'application/json' }), name('tmbcard'));
    }
    function loadProject(file) {
        var fr = new FileReader();
        fr.onload = function () {
            try {
                var d = JSON.parse(fr.result);
                if (!d || !d.data) throw new Error('bad file');
                pushUndo();
                doc = Object.assign(defaultDoc(), d);
                if (!doc.overrides) doc.overrides = { front: {}, back: {} };
                pristine = false;
                if (doc.logo && doc.logo.src) preloadImage(doc.logo.src, draw);
                qrCache = {};
                syncFormFromDoc();
                draw();
                window.__toast && window.__toast(t('project_loaded'), 'success');
            } catch (e) { window.__handleError && window.__handleError(e, SLUG); }
        };
        fr.readAsText(file);
    }
    function preloadImage(src, cb) {
        if (images[src]) { cb && cb(); return; }
        var img = new Image();
        /* The print simulation is built HERE rather than at the upload, because
         * this is the one funnel every path goes through: a restored draft and
         * a reopened project bring a logo without anyone touching the file
         * picker, and a card that previews differently after a reload is worse
         * than one that never simulated at all. */
        img.onload = function () { buildPrintSim(src); cb && cb(); };
        img.onerror = function () { cb && cb(); };
        img.src = src;
        images[src] = img;
    }

    /* ── form wiring ────────────────────────────────────────────────────────*/
    var FIELDS = ['name', 'role', 'company', 'tagline', 'phone', 'phone2',
                  'email', 'site', 'telegram', 'whatsapp', 'address'];

    function setField(f, v) {
        doc.data[f] = v;
        noteRosterOwned(f);
        var el = $('#bc-f-' + f);
        if (el && el.value !== v) el.value = v;
        qrCache = {};
        draw();
    }

    function bindForm() {
        FIELDS.forEach(function (f) {
            var el = $('#bc-f-' + f);
            if (!el) return;
            el.value = doc.data[f] || '';
            var typing = false;
            el.addEventListener('focus', function () { typing = false; });
            el.addEventListener('input', function () {
                /* ⚠️ NOTHING IS CLEARED HERE. This used to wipe every other
                 * sample field on the first keystroke; see the note by SAMPLE
                 * above for why that was removed. Leftover sample values are
                 * reported by preflight instead. */
                if (pristine) { pristine = false; pushUndo(); }
                else if (!typing) { typing = true; pushUndo(); }
                once('edit', function () { stage('editing'); });
                doc.data[f] = el.value;
                noteRosterOwned(f);
                qrCache = {};
                draw();
            });
            el.addEventListener('blur', function () { typing = false; });
        });

        bindSelect('#bc-format', function (v) { doc.format = v; });
        bindCheck('#bc-portrait', function (v) { doc.portrait = v; });
        ['#bc-sheet-size', '#bc-sheet-bleeds'].forEach(function (sel) {
            var e = $(sel);
            if (e) e.addEventListener('change', renderSheetNote);
        });
        bindSelect('#bc-radius', function (v) { doc.radius = parseFloat(v) || 0; });
        bindSelect('#bc-back', function (v) { doc.back = v; });
        bindSelect('#bc-font-heading', function (v) { doc.fonts.heading = v; });
        bindSelect('#bc-font-body', function (v) { doc.fonts.body = v; });
        bindRange('#bc-scale', function (v) { doc.scale = v; });
        bindRange('#bc-bleed', function (v) { doc.bleed = v; });
        bindRange('#bc-safe', function (v) { doc.safe = v; });

        $$('.bc-preset-card').forEach(function (el) {
            el.addEventListener('click', function () {
                pushUndo();
                doc.preset = el.getAttribute('data-preset');
                $$('.bc-preset-card').forEach(function (o) { o.classList.toggle('sel', o === el); });
                once('style', function () { stage('styling'); });
                feature('preset', doc.preset);
                draw();
            });
        });

        bindCheck('#bc-qr-on', function (v) {
            doc.qr.enabled = v; toggleQrPanel();
            if (v) once('qr', function () { feature('qr_on', doc.qr.mode); });
        });
        bindSelect('#bc-qr-mode', function (v) { doc.qr.mode = v; toggleQrPanel(); });
        bindSelect('#bc-qr-side', function (v) { doc.qr.side = v; });
        bindSelect('#bc-qr-ecc', function (v) { doc.qr.ecc = v; });
        bindRange('#bc-qr-size', function (v) { doc.qr.size = v; });
        ['url', 'text'].forEach(function (k) {
            var el = $('#bc-qr-' + k);
            if (el) el.addEventListener('input', function () { doc.qr[k] = el.value; qrCache = {}; draw(); });
        });

        $$('.bc-side-tab').forEach(function (el) {
            el.addEventListener('click', function () { setSide(el.getAttribute('data-side')); });
        });
        $$('.bc-guide').forEach(function (el) { el.addEventListener('change', draw); });

        on('#bc-export-pdf', exportPdf);
        on('#bc-export-sheet', exportSheet);
        on('#bc-export-batch', exportBatch);
        var rost = $('#bc-roster');
        if (rost) {
            rost.addEventListener('input', function () {
                /* ⚠️ THE TRIGGER IS THE TRANSITION, not the first keystroke of
                 * the session. Typing IS the intent — a list that appears where
                 * there was none should show up on the card, which is what
                 * «сотрудники не появляются в визитку» asked for. But "the
                 * first time anything is typed here" gets it wrong twice:
                 * clearing a list and pasting a new one leaves the card on
                 * somebody who is no longer in it, and editing a list you have
                 * deliberately stepped away from ("Моя карточка") drags you
                 * back into it.
                 *
                 * Empty → not empty is the moment that means "show me this". */
                var has = rosterPeople().length > 0;
                if (has && !rosterHadPeople && personIdx < 0) personIdx = 0;
                rosterHadPeople = has;
                renderRoster(); updateSummaries(); draw();
            });
            window.__toolSettings && window.__toolSettings.bind(SLUG, rost, 'roster', '');
            rosterHadPeople = rosterPeople().length > 0;
            renderRoster();
        }
        var person = $('#bc-person');
        if (person) person.addEventListener('change', function () {
            personIdx = parseInt(person.value, 10) || 0;
            feature('roster', 'preview');
            draw();
        });
        on('#bc-export-batch-main', exportBatch);
        on('#bc-sample-clear', clearSample);
        on('#bc-export-png', exportPng);
        on('#bc-save-project', saveProject);
        on('#bc-undo', undo);
        on('#bc-redo', redo);
        on('#bc-reset', function () {
            pushUndo();
            doc = defaultDoc(); pristine = true; qrCache = {};
            photoCache = {}; photoOrder = []; personIdx = PERSON_OWN; rosterHadPeople = false;
            try { window.__toolSettings && window.__toolSettings.save(SLUG, DRAFT_KEY, null); } catch (_) { }
            syncFormFromDoc(); draw();
        });

        var lp = $('#bc-load-project');
        if (lp) lp.addEventListener('change', function () {
            if (lp.files && lp.files[0]) loadProject(lp.files[0]);
            /* Clearing the input is what makes loading the SAME file twice work
               — a live FileList keeps the old entry and `change` never fires. */
            lp.value = '';
        });

        bindLogo();
        bindBg();

        document.addEventListener('keydown', function (e) {
            var ctl = interact[side];
            if (ctl && ctl.editing()) return;
            var tag = (e.target && e.target.tagName) || '';
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
            if ((e.ctrlKey || e.metaKey) && (window.__keyLetter ? window.__keyLetter(e) : String(e.key || '').toLowerCase()) === 'z') {
                e.preventDefault();
                if (e.shiftKey) redo(); else undo();
            }
        });
    }

    function setSide(s) {
        side = s;
        setBgRangeUi();
        $$('.bc-side-tab').forEach(function (o) { o.classList.toggle('sel', o.getAttribute('data-side') === s); });
        $$('.bc-cardwrap').forEach(function (o) { o.hidden = o.getAttribute('data-side') !== s; });
        draw();
    }

    function on(sel, fn) {
        var el = $(sel);
        if (el) el.addEventListener('click', function (e) { e.preventDefault(); fn(); });
    }
    function bindSelect(sel, fn) {
        var el = $(sel);
        if (!el) return;
        el.addEventListener('change', function () { pushUndo(); fn(el.value); qrCache = {}; draw(); });
    }
    function bindCheck(sel, fn) {
        var el = $(sel);
        if (!el) return;
        el.addEventListener('change', function () { pushUndo(); fn(el.checked); qrCache = {}; draw(); });
    }
    function bindRange(sel, fn) {
        var el = $(sel);
        if (!el) return;
        var out = $(sel + '-out');
        var pushed = false;
        el.addEventListener('pointerdown', function () { pushed = false; });
        var apply = function (record) {
            var v = parseFloat(el.value);
            if (record && !pushed) { pushed = true; pushUndo(); }
            fn(v);
            if (out) out.textContent = el.value;
            qrCache = {};
            draw();
        };
        el.addEventListener('input', function () { apply(true); });
        apply(false);
    }
    function toggleQrPanel() {
        var body = $('#bc-qr-body');
        if (body) body.hidden = !doc.qr.enabled;
        var m = doc.qr.mode;
        var u = $('#bc-qr-url-row'), x = $('#bc-qr-text-row');
        if (u) u.hidden = m !== 'url';
        if (x) x.hidden = m !== 'custom';
    }

    /* ── the background panel ────────────────────────────────────────────────
     * Every control here edits the background of the side CURRENTLY ON SCREEN.
     * A "which side" dropdown next to a card you can already see would be a
     * second way to say the same thing, and the two would disagree the first
     * time somebody changed one and not the other.
     */
    function bindBg() {
        var inp = $('#bc-bg-input');
        if (inp) inp.addEventListener('change', function () {
            var f = inp.files && inp.files[0];
            if (f) acceptBg(f);
            inp.value = '';
        });
        /* The section keeps only the upload; everything else about the
         * picture is under the card, where the picture is. This button is the
         * section's door to it — for the reader who is looking at the section
         * and has not yet learned that the photo can be clicked. */
        on('#bc-bg-setup', function () {
            if (!bgOf(side)) return;
            interact[side] && interact[side].select('bg');
            placeInspector();
            feature('bg', 'setup_button');
        });
        on('#bc-bg-replace', function () {
            var inp = $('#bc-bg-input');
            if (inp) inp.click();
        });
        on('#bc-bg-clear', function () {
            pushUndo();
            setBg(side, null);
            /* The framing is an override on the element, and the element is
             * gone: leaving a stale pan behind means the NEXT photograph opens
             * half off the card for no reason the user can see. */
            if (doc.overrides[side]) delete doc.overrides[side].bg;
            feature('bg', 'clear');
            draw();
        });
        on('#bc-bg-copy', function () {
            var src = bgOf(side);
            if (!src) return;
            pushUndo();
            var other = side === 'front' ? 'back' : 'front';
            setBg(other, Object.assign({}, src));
            feature('bg', 'copy');
            draw();
        });
        on('#bc-bg-fit-veil', function () {
            if (!bgOf(side)) return;
            pushUndo();
            var fit = fitVeil(side);
            setBgRangeUi();
            draw();
            feature('bg', 'veil_button');
            window.__toast && window.__toast(
                fit && fit.err === 0 ? t('bg_veil_fitted', { v: fit.light }) : t('bg_veil_no_fit'),
                fit && fit.err === 0 ? 'success' : 'warning');
        });
        on('#bc-bg-reframe', function () {
            pushUndo();
            if (doc.overrides[side]) delete doc.overrides[side].bg;
            setBgRangeUi();
            draw();
        });
        bindBgRange('#bc-bg-light', 'light', 1);
        bindBgRange('#bc-bg-blur', 'blur', 1);
        bindBgRange('#bc-bg-sat', 'sat', 1);
        bindBgRange('#bc-bg-tint', 'tint', 1);
        bindSelect('#bc-bg-fit', function (v) {
            if (!bgOf(side)) return;
            setBg(side, { fit: v });
            feature('bg', 'fit_' + v);
        });
        var z = $('#bc-bg-zoom');
        if (z) z.addEventListener('input', function () {
            var v = parseFloat(z.value) || 1;
            var o = $('#bc-bg-zoom-out'); if (o) o.textContent = Math.round(v * 100) / 100;
            /* Zoom is the element's own scale override, exactly like the logo's
             * — so the grip on the card and this slider are one value, and undo
             * covers both. */
            setOverride('bg', { scale: v });
        });
    }

    /* Ranges write straight into the side's background and redraw. `pushUndo`
     * fires on the first move of a drag only: one undo step per gesture, not
     * one per pixel of slider travel. */
    function bindBgRange(sel, key, round) {
        var el = $(sel);
        if (!el) return;
        var dragging = false;
        el.addEventListener('input', function () {
            if (!bgOf(side)) return;
            if (!dragging) { dragging = true; pushUndo(); }
            var v = parseFloat(el.value);
            var patch = {}; patch[key] = v;
            setBg(side, patch);
            var out = $(sel + '-out');
            if (out) out.textContent = round ? (Math.round(v * 10) / 10) : v;
            draw();
        });
        el.addEventListener('change', function () {
            dragging = false;
            once('bgtune', function () { feature('bg', 'tuned'); });
        });
    }

    function acceptBg(file) {
        var fr = new FileReader();
        fr.onload = function () {
            pushUndo();
            var src = fr.result;
            setBg(side, Object.assign({}, window.CardPhoto ? CardPhoto.DEFAULTS : {}, { src: src }));
            /* A new picture starts unframed. Keeping the previous photograph's
             * pan is the same trap the logo scale fell into: the number is
             * legal, the shape it was chosen for is gone. */
            if (doc.overrides[side]) delete doc.overrides[side].bg;
            preloadImage(src, function () {
                feature('bg', 'set');
                once('style', function () { stage('styling'); });
                /* Fit before the first draw the user sees, so a photo card
                 * opens readable instead of opening broken and explaining. */
                var fit = fitVeil(side);
                draw();
                /* Selected on arrival, so the panel that turns, frames and
                 * veils it is on screen the moment there is something to
                 * turn — the reader asked for the picture, not for a hunt. */
                interact[side] && interact[side].select('bg');
                placeInspector();
                if (fit && fit.light) {
                    feature('bg', 'veil_fitted', { v: fit.light });
                    window.__toast && window.__toast(t('bg_veil_fitted', { v: fit.light }), 'info');
                } else if (fit && fit.err) {
                    /* Mixed light and dark type over one picture: no single
                     * veil serves both, and saying so beats moving the handle
                     * to 100 and calling it done. */
                    window.__toast && window.__toast(t('bg_veil_no_fit'), 'warning');
                }
            });
        };
        fr.readAsDataURL(file);
    }

    function setBgRangeUi() {
        var b = bgOf(side) || (window.CardPhoto ? CardPhoto.DEFAULTS : {});
        setRange('#bc-bg-light', b.light == null ? 0 : b.light);
        setRange('#bc-bg-blur', b.blur == null ? 0 : b.blur);
        setRange('#bc-bg-sat', b.sat == null ? 100 : b.sat);
        setRange('#bc-bg-tint', b.tint == null ? 0 : b.tint);
        setVal('#bc-bg-fit', b.fit || 'cover');
        var ov = (doc.overrides[side] || {}).bg || {};
        setRange('#bc-bg-zoom', ov.scale || 1);
    }

    function syncBgUi() {
        var body = $('#bc-bg-body');
        if (!body) return;
        /* ⚠️ THE SLIDERS ARE RE-READ FROM THE DOCUMENT ON EVERY DRAW, not only
         * when a side is switched. A background changes underneath this panel
         * from at least five places that never touch a slider — undo, redo, an
         * opened project, a restored draft, "same on the other side" — and each
         * one used to leave the controls showing the previous values. A panel
         * that says 0 while the card is veiled 25 % is worse than no panel:
         * the next drag jumps the picture to whatever the lying handle said. */
        setBgRangeUi();
        var b = bgOf(side);
        syncBgBody();
        var setup = $('#bc-bg-setup'); if (setup) setup.hidden = !b;
        var lbl = $('#bc-bg-side');
        if (lbl) lbl.textContent = t(side === 'front' ? 'front' : 'back');
        var info = $('#bc-bg-info');
        if (!info) return;
        if (!b) { info.textContent = ''; info.className = 'bc-note'; return; }
        var img = images[b.src];
        var it = photoItemOf(side);
        if (!img || !img.naturalWidth) { info.textContent = '…'; info.className = 'bc-note'; return; }
        var dpi = it && it.dpi ? it.dpi : 0;
        info.textContent = t('bg_info', {
            w: img.naturalWidth, h: img.naturalHeight, dpi: dpi,
        });
        /* The resolution verdict is written where the picture was chosen, not
         * only in the preflight list: this is the moment somebody can still
         * pick a different file. */
        var lim = Spec.LIMITS;
        info.className = 'bc-note' + (dpi && dpi < lim.minDpi ? ' err'
            : (dpi && dpi < lim.goodDpi - 5 ? ' warn' : ' ok'));
    }

    function photoItemOf(which) {
        if (!lastBuild || !lastBuild[which]) return null;
        var its = lastBuild[which].items;
        for (var i = 0; i < its.length; i++) if (its[i].type === 'photo') return its[i];
        return null;
    }

    function bindLogo() {
        var zone = $('#bc-logo-input');
        if (zone) zone.addEventListener('change', function () {
            var f = zone.files && zone.files[0];
            if (f) acceptLogo(f);
            zone.value = '';
        });
        bindCheck('#bc-logo-mono', function (v) { if (doc.logo) doc.logo.mono = v; });
        on('#bc-logo-clear', function () {
            pushUndo();
            doc.logo = null;
            var i = $('#bc-logo-info'); if (i) i.textContent = '';
            var m = $('#bc-logo-mono-row'); if (m) m.hidden = true;
            draw();
        });
    }
    function acceptLogo(file) {
        var fr = new FileReader();
        fr.onload = function () {
            pushUndo();
            var src = fr.result;
            doc.logo = { src: src, mono: !!($('#bc-logo-mono') && $('#bc-logo-mono').checked) };
            preloadImage(src, function () {
                var img = images[src], info = $('#bc-logo-info');
                if (info && img && img.naturalWidth) {
                    info.textContent = t('logo_info', {
                        w: img.naturalWidth, h: img.naturalHeight, kb: Math.round(file.size / 1024),
                    });
                }
                var m = $('#bc-logo-mono-row'); if (m) m.hidden = false;
                feature('logo', /svg/.test(file.type) ? 'svg' : 'raster');
                refitLogoScale();
                draw();
                interact[side] && interact[side].select('logo');
                placeInspector();
            });
        };
        fr.readAsDataURL(file);
    }

    /* A scale that fitted the previous logo can be illegal for this one.
     *
     * The override lives on the ELEMENT (`logo`), not on the file, so swapping
     * a square mark for a 3:1 wordmark keeps the multiplier and triples the
     * width. Measured 2026-08-23: 2.5x on a square mark is 20 mm and correct;
     * the same 2.5x on the wordmark that replaced it was 97.8 mm on a 90 mm
     * card, hanging 11.8 mm past the bleed — and the growth clamp cannot catch
     * it, because the person never pressed the button.
     *
     * So the multiplier is re-validated against the NEW shape, on both sides:
     * the card the logo sits on may not be the one being edited. */
    function refitLogoScale() {
        ['front', 'back'].forEach(function (which) {
            var over = (doc.overrides[which] || {}).logo;
            if (!over || !over.scale || over.scale <= 1) return;
            var was = side;
            side = which;
            try {
                if (logoFits('logo', over.scale)) return;
                var lo = 1, hi = over.scale;
                for (var i = 0; i < 10; i++) {
                    var mid = (lo + hi) / 2;
                    if (logoFits('logo', mid)) lo = mid; else hi = mid;
                }
                over.scale = Math.round(lo * 1000) / 1000;
            } finally { side = was; }
        });
    }

    function syncFormFromDoc() {
        FIELDS.forEach(function (f) { var el = $('#bc-f-' + f); if (el) el.value = doc.data[f] || ''; });
        setBgRangeUi();
        setVal('#bc-format', doc.format); setVal('#bc-back', doc.back);
        setVal('#bc-font-heading', doc.fonts.heading); setVal('#bc-font-body', doc.fonts.body);
        setVal('#bc-radius', String(doc.radius));
        setRange('#bc-scale', doc.scale); setRange('#bc-qr-size', doc.qr.size);
        var p = $('#bc-portrait'); if (p) p.checked = !!doc.portrait;
        var q = $('#bc-qr-on'); if (q) q.checked = !!doc.qr.enabled;
        /* Поля QR — тоже из документа: черновик, проект и визитка эксперта
         * приносят режим «ссылка» с адресом, а форма до 22.10.2026 показывала
         * «vCard» и пустое поле при QR, который печатал ссылку. */
        setVal('#bc-qr-mode', doc.qr.mode); setVal('#bc-qr-side', doc.qr.side); setVal('#bc-qr-ecc', doc.qr.ecc);
        setVal('#bc-qr-url', doc.qr.url || ''); setVal('#bc-qr-text', doc.qr.text || '');
        $$('.bc-preset-card').forEach(function (o) { o.classList.toggle('sel', o.getAttribute('data-preset') === doc.preset); });
        $$('.bc-pal').forEach(function (o) { o.classList.toggle('sel', o.getAttribute('data-pal') === doc.palette); });
        toggleQrPanel();
        syncUndoButtons();
    }
    function setVal(sel, v) { var el = $(sel); if (el) el.value = v; }
    function setRange(sel, v) {
        var el = $(sel); if (!el) return;
        el.value = v;
        var out = $(sel + '-out'); if (out) out.textContent = v;
    }

    /* The verdict is the honest half of a brand-colour picker: it says how far
     * the chosen colour will move on paper BEFORE anything is printed, using
     * the same ink model the preview draws with. */
    function renderCustomVerdict() {
        var el = $('#bc-custom-verdict');
        if (!el) return;
        var rgb = Ink.hexToRgb(doc.customHex);
        if (!rgb) { el.textContent = ''; return; }
        var g = Ink.gamutCheck(rgb[0], rgb[1], rgb[2]);
        el.className = 'bc-note ' + (g.level === 'ok' ? 'ok' : (g.level === 'shift' ? 'warn' : 'err'));
        el.innerHTML = '<i class="bc-swatch" style="background:' + Ink.rgbToHex(g.printed) + '"></i>' +
            esc(t('gamut_' + g.level, { shift: g.shift }));
    }

    function bindCustomColour() {
        var col = $('#bc-custom-color'), hex = $('#bc-custom-hex');
        if (!col || !hex) return;
        var apply = function (v, record) {
            if (!Ink.hexToRgb(v)) return;
            if (record) pushUndo();
            doc.customHex = v.toUpperCase();
            doc.palette = 'custom';
            once('style', function () { stage('styling'); });
            var _rgb = Ink.hexToRgb(doc.customHex);
            if (_rgb) {
                var _g = Ink.gamutCheck(_rgb[0], _rgb[1], _rgb[2]);
                feature('custom_color', _g.level, { shift: _g.shift });
            }
            col.value = v.toLowerCase();
            if (hex.value.toUpperCase() !== doc.customHex) hex.value = doc.customHex;
            $$('.bc-pal').forEach(function (o) { o.classList.remove('sel'); });
            draw();
        };
        var pushed = false;
        col.addEventListener('pointerdown', function () { pushed = false; });
        col.addEventListener('input', function () {
            if (!pushed) { pushed = true; pushUndo(); }
            apply(col.value, false);
        });
        hex.addEventListener('input', function () { apply(hex.value.trim(), true); });
    }

    function paintPalettes() {
        var host = $('#bc-palettes');
        if (!host) return;
        host.innerHTML = Ink.palettes().map(function (p) {
            var chips = ['ink', 'accent', 'muted', 'paper'].map(function (r) {
                return '<b style="background:' + Render.css(p[r]) + '"></b>';
            }).join('');
            return '<button type="button" class="bc-pal' + (p.id === doc.palette ? ' sel' : '') +
                '" data-pal="' + p.id + '"><i>' + chips + '</i><span>' + esc(t('pal_' + p.id)) + '</span></button>';
        }).join('');
        $$('.bc-pal', host).forEach(function (el) {
            el.addEventListener('click', function () {
                pushUndo();
                doc.palette = el.getAttribute('data-pal');
                $$('.bc-pal').forEach(function (o) { o.classList.toggle('sel', o === el); });
                once('style', function () { stage('styling'); });
                feature('palette', doc.palette);
                draw();
            });
        });
    }

    /* ── the draft ───────────────────────────────────────────────────────────
     * Somebody spends ten minutes on a card, the tab reloads, and everything is
     * gone. A design tool that loses work to a stray refresh is not one people
     * come back to, and "Сохранить проект" only helps the person who thought to
     * press it BEFORE the accident.
     *
     * Restored silently rather than behind a prompt — an editor that reopens
     * what you were doing is the expectation, and a dialog on arrival is a
     * worse first screen than the finished sample. A quiet line says what
     * happened and offers the way back to a blank one.
     *
     * ⚠️ The logo is dropped from the draft when the document gets big.
     * localStorage is a handful of megabytes and a data-URL logo can be most of
     * it; a QuotaExceededError thrown mid-typing would take the whole autosave
     * down silently, which is worse than a draft without its picture. */
    var DRAFT_KEY = 'draft';
    var DRAFT_MAX = 1200000;
    var draftTimer = null;

    function saveDraft() {
        if (!window.__toolSettings || pristine) return;
        clearTimeout(draftTimer);
        draftTimer = setTimeout(function () {
            try {
                var d = snapshot();
                d.__at = Date.now();
                var s = JSON.stringify(d);
                /* Backgrounds go first and logos second: a photograph is an
                 * order of magnitude the bigger of the two, and a draft that
                 * keeps the mark but loses the picture is more of the card back
                 * than one that keeps neither. */
                if (s.length > DRAFT_MAX && d.bg && (d.bg.front || d.bg.back)) {
                    d.bg = { front: null, back: null };
                    d.__bgDropped = 1;
                    s = JSON.stringify(d);
                }
                if (s.length > DRAFT_MAX && d.logo) {
                    d.logo = null;
                    d.__logoDropped = 1;
                    s = JSON.stringify(d);
                }
                if (s.length > DRAFT_MAX) return;
                window.__toolSettings.save(SLUG, DRAFT_KEY, d);
            } catch (_) { /* private mode, quota, disabled storage — never fatal */ }
        }, 800);
    }

    function restoreDraft() {
        if (!window.__toolSettings) return false;
        var d = null;
        try { d = window.__toolSettings.load(SLUG, DRAFT_KEY, null); } catch (_) { return false; }
        if (!d || !d.data) return false;
        /* A draft with nothing in it is not worth restoring over the sample. */
        var filled = FIELDS.filter(function (f) { return (d.data[f] || '').trim(); }).length;
        if (!filled) return false;
        doc = Object.assign(defaultDoc(), d);
        if (!doc.overrides) doc.overrides = { front: {}, back: {} };
        pristine = false;
        if (doc.logo && doc.logo.src) preloadImage(doc.logo.src, draw);
        showDraftNote(d.__at, d.__logoDropped);
        feature('draft', 'restored', { fields: filled });
        return true;
    }

    function showDraftNote(at, logoDropped) {
        var host = $('#bc-hint');
        if (!host) return;
        var when = at ? new Date(at) : null;
        var ago = when ? Math.max(1, Math.round((Date.now() - at) / 60000)) : null;
        host.className = 'bc-note ok';
        host.innerHTML = esc(t('draft_restored', { min: ago == null ? '—' : ago })) +
            (logoDropped ? ' ' + esc(t('draft_no_logo')) : '') +
            ' <button type="button" id="bc-draft-drop" style="background:none;border:0;color:var(--accent);' +
            'cursor:pointer;font:inherit;padding:0;text-decoration:underline">' + esc(t('draft_start_over')) + '</button>';
        var b = $('#bc-draft-drop');
        if (b) b.addEventListener('click', function () {
            try { window.__toolSettings.save(SLUG, DRAFT_KEY, null); } catch (_) { }
            doc = defaultDoc(); pristine = true; qrCache = {};
            undoStack.length = 0; redoStack.length = 0;
            host.className = 'bc-note';
            host.textContent = t('canvas_hint');
            syncFormFromDoc(); draw();
            feature('draft', 'discarded');
        });
    }

    /* ── визитка эксперта из раздела «Эксперты» ─────────────────────────────
     * Набор «свои клиенты» в панели эксперта ведёт сюда с данными его
     * публичной страницы во ФРАГМЕНТЕ адреса (#expert=1&name=…&role=…&site=…
     * &qr=…): страница тула одна на всех в кэше, а фрагмент до сервера не
     * доходит. Телефон и почта не едут — их человек впишет сам. Черновик
     * важнее: при нём данные эксперта предлагаются кнопкой, одной отменой назад. */
    function readExpertPrefill() {
        var h = (location.hash || '').replace(/^#/, '');
        if (h.indexOf('expert=1') === -1) return null;
        var p;
        try { p = new URLSearchParams(h); } catch (_) { return null; }
        var clip = function (k, n) { return String(p.get(k) || '').slice(0, n).trim(); };
        var qr = clip('qr', 400);
        if (!/^https:\/\//i.test(qr)) qr = '';
        var pre = { name: clip('name', 80), role: clip('role', 120), site: clip('site', 120), qr: qr };
        try { history.replaceState(null, '', location.pathname + location.search); } catch (_) { }
        return (pre.name || pre.qr) ? pre : null;
    }
    function applyExpertPrefill(pre) {
        var d = {};
        Object.keys(sampleData()).forEach(function (k) { d[k] = ''; });
        d.name = pre.name; d.role = pre.role; d.site = pre.site;
        doc.data = d;
        if (pre.qr) doc.qr = Object.assign({}, doc.qr, { enabled: true, mode: 'url', url: pre.qr });
        pristine = false;
        qrCache = {};
        feature('prefill', 'expert');
    }
    function offerExpertPrefill(pre) {
        var host = $('#bc-hint');
        if (!host) return;
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'bc-expert-fill';
        b.style.cssText = 'background:none;border:0;color:var(--accent);cursor:pointer;font:inherit;padding:0;text-decoration:underline;margin-inline-start:6px';
        b.textContent = t('expert_prefill');
        b.addEventListener('click', function () {
            pushUndo();
            applyExpertPrefill(pre);
            syncFormFromDoc();
            draw();
            b.remove();
        });
        host.appendChild(document.createTextNode(' '));
        host.appendChild(b);
    }

    /* ── boot ───────────────────────────────────────────────────────────────*/
    function boot() {
        measure = Render.measurer(familyFor);
        paintPalettes();
        bindCustomColour();
        bindForm();
        bindSections();
        var expertPre = readExpertPrefill();
        if (restoreDraft()) {
            if (expertPre) offerExpertPrefill(expertPre);
        } else if (expertPre) {
            applyExpertPrefill(expertPre);
        }
        syncFormFromDoc();

        ['front', 'back'].forEach(function (which) {
            var host = $('#bc-wrap-' + which);
            if (!host) return;
            interact[which] = Interact.mount(host, {
                side: which,
                t: t,
                bounds: boundsOf,
                getItems: function () { return (lastBuild && lastBuild[which] && lastBuild[which].items) || []; },
                getView: function () { return viewState[which] || { scale: 1, padX: 0, padY: 0 }; },
                getIssues: function () { return lastIssues; },
                getOverride: function (id) { return (doc.overrides[which] || {})[id]; },
                getQrSize: function () { return doc.qr.size; },
                getFieldValue: function (f) { return doc.data[f]; },
                onSelect: function (id) {
                    if (id) {
                        once('manip', function () { stage('refining'); });
                        feature('manip', 'select', { el: id });
                    }
                    renderCtx();
                    placeInspector();
                    if (lastPre) renderPreflight(lastPre);
                },
                onGestureStart: pushUndo,
                onGestureEnd: function () { renderCtx(); },
                onOverride: function (id, patch) {
                    once('manip', function () { stage('refining'); });
                    once('manip:' + (patch.scale != null ? 'resize' : 'move'), function () {
                        feature('manip', patch.scale != null ? 'resize' : 'move', { el: id });
                    });
                    if (!doc.overrides[which]) doc.overrides[which] = {};
                    doc.overrides[which][id] = Object.assign({}, doc.overrides[which][id] || {}, patch);
                    draw();
                },
                onResetOverride: function (id) {
                    if (doc.overrides[which]) delete doc.overrides[which][id];
                    draw();
                },
                onQrSize: setQrSize,
                onEdit: function (field, value, id) {
                    pushUndo();
                    pristine = false;
                    /* ⚠️ NO WIPE. Editing one word ON THE CARD used to empty
                     * every other field — the exact report this rebuild started
                     * from. See SAMPLE above. */
                    if (field) setField(field, value);
                    /* No field behind the block (the monogram, a preset's own
                     * kicker): the typed string lives on the element, so
                     * "вернуть как было" brings the computed value back. */
                    else if (id) setOverride(id, { text: value });
                    once('manip', function () { stage('refining'); });
                    feature('manip', 'inline_edit', { el: field || id });
                },
                onIssueClick: function (iss) {
                    var pf = $('#bc-pf');
                    if (pf && !pf.open) pf.open = true;
                    feature('flag_click', iss.code);
                },
            });
        });

        var fontsReady = document.fonts && document.fonts.ready
            ? Promise.race([document.fonts.ready, delay(2500)]) : Promise.resolve();
        fontsReady.then(function () { draw(); stage('ready'); });

        Pdf.ensureQr().then(function (l) { qrLib = l; qrCache = {}; draw(); }).catch(function () {});

        var ro = window.ResizeObserver ? new ResizeObserver(debounce(draw, 60)) : null;
        if (ro) $$('.bc-cardwrap').forEach(function (el) { ro.observe(el); });
        window.addEventListener('resize', debounce(draw, 120));
        /* What the reader pressed last decides where the inspector opens on a
         * phone (placeInspector). A keyboard press counts like a tap; a press
         * on the card brings an open inspector back under it. */
        ['pointerdown', 'keydown'].forEach(function (type) {
            document.addEventListener(type, function (e) {
                if (type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
                lastPress = e.target;
                if (type === 'pointerdown' && e.target.closest && e.target.closest('.bc-cardwrap')) placeInspector();
            }, true);
        });
        /* A section that closes takes nothing of the inspector with it. */
        $$('details.bc-sec').forEach(function (d) {
            d.addEventListener('toggle', function () {
                var insp = $('#bc-insp');
                if (!d.open && insp && inspHome && d.contains(insp)) inspHome.after(insp);
            });
        });
        if (PHONE) {
            var toDesktop = function () { if (!PHONE.matches) placeInspector(); };
            if (PHONE.addEventListener) PHONE.addEventListener('change', toDesktop);
            else if (PHONE.addListener) PHONE.addListener(toDesktop);
        }
    }
    /* ── one section open at a time ─────────────────────────────────────────
     * `<details name>` makes the group exclusive natively (Chrome 120, Safari
     * 17.2, Firefox 130); this is the same rule for browsers before that.
     * Measured 2026-08-26, before: three sections open by default made the
     * column 2240 px tall in a 900 px window, and the section on screen was
     * whichever one had been opened last, not the one being worked in. */
    function bindSections() {
        var secs = $$('details.bc-sec');
        secs.forEach(function (d) {
            d.addEventListener('toggle', function () {
                if (!d.open) return;
                secs.forEach(function (o) { if (o !== d && o.open) o.open = false; });
            });
        });
    }

    function delay(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
    function debounce(fn, ms) { var t2; return function () { clearTimeout(t2); t2 = setTimeout(fn, ms); }; }

    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();

    /* Test seam: the playwright suite drives the real document. */
    window.__BC = {
        doc: function () { return doc; },
        set: function (patch) { Object.assign(doc, patch); pristine = false; syncFormFromDoc(); draw(); },
        build: buildAll,
        boxes: boxes,
        side: function () { return side; },
        setSide: setSide,
        issues: function () { return lastIssues; },
        select: function (id) { interact[side] && interact[side].select(id); },
        selected: function () { return interact[side] && interact[side].selected(); },
        undo: undo, redo: redo, canUndo: function () { return undoStack.length; },
        preflight: function () {
            var built = buildAll();
            return Pre.run({
                boxes: boxes(), limits: Spec.LIMITS, ink: Ink, measure: measure,
                palette: palette(), doc: doc, sample: sampleState(),
                sides: [
                    { side: 'front', items: built.front.items, warnings: built.front.warnings },
                    { side: 'back', items: built.back.items, warnings: built.back.warnings },
                ],
            });
        },
        pdf: function (extra) {
            return Pdf.cardPdf({ boxes: boxes(), sides: sidesForExport(), opts: exportOpts(extra) });
        },
        /* The batch engine without the cap in front of it. Only a test has a
         * reason to call this: it answers "what does the PDF cost to build",
         * which is a different question from "how many may this tier make". */
        batchBytes: function () {
            var people = rosterPeople();
            if (!people.length) return Promise.resolve(new Uint8Array(0));
            return Pdf.batchPdf({
                boxes: boxes(),
                people: people.map(function (d) { return { sides: sidesFor(d) }; }),
                opts: exportOpts(),
            });
        },
        roster: rosterPeople,
        person: function (i) { if (i != null) { personIdx = i; draw(); } return personIdx; },
        personOwn: PERSON_OWN,
        setBg: function (which, patch) { pushUndo(); setBg(which, patch); draw(); },
        /* The fit runs inside `acceptBg`, i.e. only on a real file dialog. A
         * test that drives `setBg` would otherwise measure the UNFITTED card
         * and report the feature as absent — which is exactly what the first
         * measurement of it did. */
        fitVeil: function (which) { var r = fitVeil(which || side); setBgRangeUi(); draw(); return r; },
        photoItem: photoItemOf,
        openSection: openSection,
        imposition: sheetImposition,
        sample: sampleState,
        clearSample: clearSample,
        sheet: function (extra) {
            var imp = Spec.imposition(fmt(), { bleed: doc.bleed, safe: doc.safe,
                portrait: doc.portrait, bleeds: !!(extra && extra.bleeds), sheet: { w: 210, h: 297 } });
            return Pdf.sheetPdf({ boxes: boxes(), sides: sidesForExport(),
                imposition: imp, opts: exportOpts(Object.assign({ marks: false }, extra)) });
        },
        draw: draw,
    };
})();
